import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { AnimatePresence, motion, useInView, animate } from 'framer-motion';
import { Link, Route, Switch, useLocation, useParams } from 'wouter';
import { 
  ArrowRight, ArrowUpRight, Award, Check, CheckCircle2, ChevronDown, ChevronLeft, ChevronRight, ChevronUp,
  ExternalLink, Factory, FileCheck2, GlassWater, Images, Instagram, Layers, MapPin, Maximize2, Menu, Phone, Plus, Ruler, Send, 
  ShieldCheck, Sparkles, Trash2, X, Wrench, Zap 
} from 'lucide-react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ErrorBoundary } from '@/components/error-boundary';
import { Toaster } from '@/components/ui/toaster';
import { TooltipProvider } from '@/components/ui/tooltip';
import NotFound from '@/pages/not-found';
import '@/index.css';
import { SEO } from '@/components/SEO';

import { localImages as pics } from '@/assets/imgs';
import { projects, type Project } from '@/data/projects';
import { ProjectGalleryModal } from '@/components/ProjectGalleryModal';
import { windowColors, type WindowColor } from '@/data/colors';
import { ColorPicker } from '@/components/ColorPicker';
import { submitLead, type LeadProductItem } from '@/services/leadService';
import { translations, type Lang, type TranslationDictionary } from '@/data/translations';
import { getTranslatedProduct } from '@/data/productTranslations';
import { formatPhoneNumber, handlePhoneKeyDown } from '@/lib/utils';
import {
  FORM_CATEGORIES,
  getFormCategory,
  getFormCategoryOptions,
  getFormProductOptions,
  createDefaultFormItem,
  CUSTOM_PRODUCT_VALUE,
  type FormProductItem
} from '@/data/formCategories';

const queryClient = new QueryClient();

const copy = (lang: Lang): TranslationDictionary => translations[lang] || translations.ru;

export type Category = {
  slug: string;
  title: string;
  subtitle: string;
  image: string;
  description: string;
};

export type Product = {
  slug: string;
  categorySlug: string;
  subcategory?: string;
  title: string;
  brand: string;
  brandCountry?: string;
  subtitle: string;
  image: string;
  specs: string[];
  description: string;
  related?: string[];
};

export const categories: Category[] = [
  { 
    slug: 'aluminium', 
    title: 'Алюминиевые окна и двери', 
    subtitle: 'Sistem Aluminium · ASAŞ · Akfa TERMO · Aldox', 
    image: pics.termo77, 
    description: 'Лёгкие и тёплые алюминиевые конструкции: флагманские турецкие системы Sistem Aluminium (65, 75, 85 серии) и ASAŞ Rescara (64, 75 серии), энергосберегающие серии Akfa TERMO с полиамидными термомостами (70, 77, 78, 98), а также классические системы Aldox, Aldox 2 и AKF 47 Champion.' 
  },
  { 
    slug: 'pvc', 
    title: 'ПВХ окна и двери', 
    subtitle: 'Deceuninck · Akfa Engelberg · Trio · Quattro', 
    image: pics.deceuninck8000, 
    description: 'Теплосберегающие и шумозащитные ПВХ окна: бельгийские инновационные системы Deceuninck (6000, 7000, 8000 серии), швейцарские системы Engelberg (7000, 7600, 8000), а также практичные серии Akfa Trio (5800, 6000) и Akfa Quattro (5200, 5800, 6000).' 
  },
  { 
    slug: 'sliding', 
    title: 'Раздвижные и гильотинные системы', 
    subtitle: 'Гильотина · Akfa BKH · BKG · Lift & Slide', 
    image: pics.guillotine, 
    description: 'Свободный проём и панорамный обзор: автоматические гильотинные стеклянные системы вертикального подъема для ресторанов, веранд и террас, легкие раздвижные Akfa Econom BKG 40, теплые Standart BKH 38, подъемно-раздвижные порталы Standart BKH 60 и Premium BKH 65.' 
  },
  { 
    slug: 'facade', 
    title: 'Фасадные системы', 
    subtitle: 'Akfa BKF 48 · BKF 50 · BKF Max', 
    image: pics.bkf50, 
    description: 'Светопрозрачные стоечно-ригельные и структурные фасады Akfa: энергоэффективные Econom BKF 48, сейсмостойкие Premium BKF 50 (до 9 баллов) и ультратонкие фасады Premium BKF Max (22 мм).' 
  },
  { 
    slug: 'office-partitions', 
    title: 'Офисные перегородки', 
    subtitle: 'Akfa BKO 38 · BKO 40', 
    image: pics.bko40, 
    description: 'Системы интерьерных офисных перегородок Akfa BKO 38 и BKO 40 с одинарным, двойным или комбинированным заполнением для создания современных рабочих пространств и кабинетов.' 
  },
  { 
    slug: 'stained-glass', 
    title: 'Витражи и панорамное остекление', 
    subtitle: 'Свет как часть архитектуры', 
    image: pics.spiderSystem, 
    description: 'Панорамные решения с большим световым проёмом. Подбираем формулу стекла по инсоляции, нагрузке и сценарию использования пространства.' 
  },
  { 
    slug: 'rolling-shutters', 
    title: 'Рольставни и роллетные ворота', 
    subtitle: 'Akfa · защита и контроль света', 
    image: pics.rollerShutters, 
    description: 'Современная замена решёткам и жалюзи. Роллетные системы защищают проёмы, регулируют свет и аккуратно интегрируются в фасад здания.' 
  },
  { 
    slug: 'glass-railings', 
    title: 'Стеклянные перила и ограждения', 
    subtitle: 'Безопасность без визуальных границ', 
    image: pics.railings, 
    description: 'Лаконичные ограждения для лестниц, балконов и террас из закаленного триплекса. Рассчитываем крепления и выполняем аккуратный монтаж.' 
  },
  { 
    slug: 'mosquito-nets', 
    title: 'Москитные сетки', 
    subtitle: 'Рамочные решения для всех типов окон', 
    image: pics.frameNet, 
    description: 'Практичная защита от насекомых и пыли с точной посадкой в проём. Рамочные, дверные и раздвижные плиссе-системы.' 
  },
];

export const products: Product[] = [
  // ==========================================
  // --- 1. АЛЮМИНИЕВЫЕ ОКНА И ДВЕРИ (ALUMINIUM) ---
  // ==========================================
  {
    slug: 'termo-70',
    categorySlug: 'aluminium',
    subcategory: 'TERMO серия',
    title: 'Akfa TERMO 70',
    brand: 'Akfa',
    brandCountry: 'Узбекистан 🇺🇿',
    subtitle: 'Усиленная теплая алюминиевая система 70 мм',
    image: pics.termo70,
    specs: [
      'Монтажная глубина рамы: 70 мм',
      'Ширина рамы: 70 мм',
      'Ширина створки: 75,5 мм',
      'Толщина стеклопакета (min/max): 20 мм / 50 мм',
      'Ширина термобарьера: 24 мм',
      'Максимальный вес створки: окна 100 кг / двери 160 кг',
      'Тип открывания: Внутреннее открывание (окно / дверь)',
      'Применение для окон: до 2200 мм × 1200 мм',
      'Системное дверное решение: до 2800 мм × 1400 мм'
    ],
    description: 'Премиальная система Akfa TERMO 70 с повышенной жесткостью и звукоизоляцией. Позволяет устанавливать двухкамерные энергосберегающие стеклопакеты толщиной до 50 мм.',
    related: ['termo-77', 'termo-98', 'ywd-78-thermo']
  },
  {
    slug: 'termo-77',
    categorySlug: 'aluminium',
    subcategory: 'TERMO серия',
    title: 'Akfa TERMO 77',
    brand: 'Akfa',
    brandCountry: 'Узбекистан 🇺🇿',
    subtitle: 'Флагманская теплая алюминиевая система 77 мм',
    image: pics.termo77,
    specs: [
      'Монтажная глубина рамы: 69 мм',
      'Ширина створки: 77 мм',
      'Ширина термобарьера окна: 34 мм',
      'Ширина термобарьера двери: 28 мм',
      'Толщина стеклопакета (min/max): 24 мм / 50 мм',
      'Ширина видимой части створки: min 67 мм / max 97 мм',
      'Максимальный вес створки: окна 100 кг / двери 160 кг',
      'Применение для окон: до 2200 мм × 1200 мм',
      'Системное дверное решение: до 3000 мм × 1600 мм'
    ],
    description: 'Максимальная теплоизоляция в линейке Akfa TERMO с мощными полиамидными термомостами до 34 мм. Идеально подходит для панорамных энергопассивных зданий и высотных резиденций.',
    related: ['termo-98', 'termo-70', 'ywd-78-thermo']
  },
  {
    slug: 'termo-98',
    categorySlug: 'aluminium',
    subcategory: 'TERMO серия',
    title: 'Akfa TERMO 98',
    brand: 'Akfa',
    brandCountry: 'Узбекистан 🇺🇿',
    subtitle: 'Сверхпрочная архитектурная термо-система 70 мм',
    image: pics.termo98,
    specs: [
      'Монтажная глубина рамы: 70 мм',
      'Толщина стеклопакета (min/max): 20 мм / 50 мм',
      'Максимальный вес створки: окна 100 кг / двери 160 кг',
      'Максимальный размер створки (окна): 2200 мм × 1200 мм',
      'Максимальный размер створки (двери): 2800 мм × 1400 мм',
      'Применение для окон: до 2200 мм × 1200 мм',
      'Системное дверное решение: до 2800 мм × 1400 мм'
    ],
    description: 'Архитектурная алюминиевая система Akfa TERMO 98 повышенной прочности для крупноформатных оконных и дверных проемов с высокими статическими и ветровыми нагрузками.',
    related: ['termo-77', 'ywd-78-thermo', 'termo-70']
  },
  {
    slug: 'ywd-78-thermo',
    categorySlug: 'aluminium',
    subcategory: 'TERMO серия',
    title: 'Akfa YWD 78 THERMO',
    brand: 'Akfa',
    brandCountry: 'Узбекистан 🇺🇿',
    subtitle: 'Премиальная термо-серия 75.5 мм для окон и входных групп',
    image: pics.ywdtermo78,
    specs: [
      'Монтажная глубина рамы: 75,5 мм',
      'Толщина стеклопакета (min/max): 20 мм / 36 мм',
      'Максимальный вес створки: окна 100 кг / двери 160 кг',
      'Максимальный размер створки (окна): 2200 мм × 1200 мм',
      'Максимальный размер створки (двери): 2800 мм × 1400 мм',
      'Применение для окон: до 2200 мм × 1200 мм',
      'Системное дверное решение: до 2800 мм × 1400 мм'
    ],
    description: 'Специализированная серия Akfa YWD 78 THERMO с монтажной глубиной 75.5 мм, обеспечивающая высочайшую стабильность геометрии при интенсивной ежедневной эксплуатации.',
    related: ['termo-77', 'termo-70', 'termo-98']
  },
  {
    slug: 'aldox',
    categorySlug: 'aluminium',
    subcategory: 'Aldox серия',
    title: 'Akfa Aldox',
    brand: 'Akfa',
    brandCountry: 'Узбекистан 🇺🇿',
    subtitle: 'Классическая алюминиевая система 45.5 мм',
    image: pics.economAldox,
    specs: [
      'Монтажная глубина рамы: 45,5 мм',
      'Толщина стеклопакета (min/max): 4 мм / 20 мм',
      'Максимальный вес створки (окна / двери): 80 кг / 140 кг',
      'Максимальный размер створки (окна): 1800 мм × 1000 мм',
      'Максимальный размер створки (двери): 2500 мм × 1500 мм',
      'Применение для окон: до 1850 мм × 1000 мм',
      'Системное дверное решение: до 2500 мм × 1500 мм'
    ],
    description: 'Проверенная временем алюминиевая система Akfa Aldox 45.5 мм для балконов, тамбуров, коммерческих входных групп и легких витражей.',
    related: ['aldox-2', 'akf-47-champion', 'termo-70']
  },
  {
    slug: 'aldox-2',
    categorySlug: 'aluminium',
    subcategory: 'Aldox серия',
    title: 'Akfa Aldox 2',
    brand: 'Akfa',
    brandCountry: 'Узбекистан 🇺🇿',
    subtitle: 'Модернизированная алюминиевая система 45.5 мм',
    image: pics.aldox2,
    specs: [
      'Монтажная глубина рамы: 45,5 мм',
      'Толщина стеклопакета (min/max): 4 мм / 20 мм',
      'Максимальный вес створки: 80 кг / 140 кг',
      'Максимальный размер створки (окна): 1800 мм × 1000 мм',
      'Максимальный размер створки (двери): 2500 мм × 1500 мм',
      'Применение для окон: до 1800 мм × 1000 мм',
      'Применение для дверей: до 2500 мм × 1500 мм'
    ],
    description: 'Обновленная модификация системы Akfa Aldox 2 с улучшенной геометрией фальца под фурнитуру и расширенными возможностями интеграции створок.',
    related: ['aldox', 'akf-47-champion', 'termo-70']
  },
  {
    slug: 'akf-47-champion',
    categorySlug: 'aluminium',
    subcategory: 'Aldox серия',
    title: 'Akfa AKF 47 CHAMPION',
    brand: 'Akfa',
    brandCountry: 'Узбекистан 🇺🇿',
    subtitle: 'Усиленная алюминиевая система 47 мм повышенной прочности',
    image: pics.akf47,
    specs: [
      'Монтажная глубина рамы: 47 мм',
      'Толщина стеклопакета (min/max): 4 мм / 24 мм',
      'Максимальный вес створки: 100 кг (окна) / 160 кг (двери)',
      'Максимальный размер створки (окна): 2200 мм × 1200 мм',
      'Максимальный размер створки (двери): 2600 мм × 1200 мм',
      'Применение для окон: до 2200 мм × 1200 мм',
      'Системное дверное решение: до 2600 мм × 1200 мм'
    ],
    description: 'Усиленная алюминиевая система Akfa AKF 47 Champion. Отличается повышенной несущей способностью дверных петель (до 160 кг) и увеличенными габаритами створок.',
    related: ['aldox', 'aldox-2', 'termo-70']
  },
  {
    slug: 'sistem-wh65',
    categorySlug: 'aluminium',
    subcategory: 'Sistem Aluminium',
    title: 'Sistem Aluminium WH65',
    brand: 'Sistem Aluminium',
    brandCountry: 'Турция 🇹🇷',
    subtitle: 'Архитектурная алюминиевая система 65 мм (Турция)',
    image: pics.sistemAl65,
    specs: [
      'Монтажная глубина рамы: 65 мм',
      'Монтажная глубина створки: 75 мм',
      'Ширина термомоста (полиамид): 20–24 мм',
      'Толщина стеклопакета: До 44 мм',
      'Теплоизоляция Uf: 1.8 – 2.2 W/m²K',
      'Шумоизоляция: до 42 дБ',
      'Европейский паз под фурнитуру: Roto / Master'
    ],
    description: 'Высокотехнологичная оконно-дверная система Sistem Aluminium WH65 с терморазрывом. Проверенное турецкое качество для жилых комплексов и коммерческих зданий.',
    related: ['sistem-wh75', 'sistem-wh85', 'asas-rwt64', 'termo-70']
  },
  {
    slug: 'sistem-wh75',
    categorySlug: 'aluminium',
    subcategory: 'Sistem Aluminium',
    title: 'Sistem Aluminium WH75',
    brand: 'Sistem Aluminium',
    brandCountry: 'Турция 🇹🇷',
    subtitle: 'Теплая архитектурная система 75 мм премиум-класса (Турция)',
    image: pics.sistemAl75,
    specs: [
      'Монтажная глубина рамы: 75 мм',
      'Монтажная глубина створки: 85 мм',
      'Терморазрыв: 24–30 мм полиамидные вставки',
      'Толщина стеклопакета: До 52 мм',
      'Теплоизоляция Uf: 1.4 – 1.7 W/m²K',
      'Высокая стойкость к ветровым нагрузкам'
    ],
    description: 'Высокоэффективная теплая алюминиевая система 75 серии от Sistem Aluminium (Турция) для крупноформатного остекления современных зданий и коттеджей.',
    related: ['sistem-wh85', 'sistem-wh65', 'asas-rwt75', 'termo-77']
  },
  {
    slug: 'sistem-wh85',
    categorySlug: 'aluminium',
    subcategory: 'Sistem Aluminium',
    title: 'Sistem Aluminium WH85',
    brand: 'Sistem Aluminium',
    brandCountry: 'Турция 🇹🇷',
    subtitle: 'Флагманская термо-система 85 мм ультра-эффективности (Турция)',
    image: pics.sistemAl85,
    specs: [
      'Монтажная глубина рамы: 85 мм',
      'Монтажная глубина створки: 95 мм',
      'Терморазрыв: 34 мм многокамерный полиамидный мост',
      'Толщина стеклопакета: До 64 мм (триплекс)',
      'Теплоизоляция Uf: до 1.1 W/m²K',
      'Шумоизоляция: до 46 дБ',
      'Максимальный вес створки: до 200 кг'
    ],
    description: 'Флагманская алюминиевая поворотно-откидная система ультра-высокой энергоэффективности Sistem Aluminium WH85. Максимальная тепло- и звукоизоляция в алюминиевом сегменте.',
    related: ['sistem-wh75', 'sistem-wh65', 'asas-rwt75', 'termo-77']
  },
  {
    slug: 'asas-rwt64',
    categorySlug: 'aluminium',
    subcategory: 'ASAŞ Rescara',
    title: 'ASAŞ Rescara RWT 64',
    brand: 'ASAŞ',
    brandCountry: 'Турция 🇹🇷',
    subtitle: 'Теплая алюминиевая система 64 мм (Турция)',
    image: pics.asas64,
    specs: [
      'Монтажная глубина рамы: 64 мм',
      'Монтажная глубина створки: 74 мм',
      'Ширина полиамидного термомоста: 24 мм',
      'Толщина стеклопакета: До 44 мм',
      'Теплоизоляция Uf: 1.9 – 2.4 W/m²K',
      'Акустическая изоляция: до 40 дБ'
    ],
    description: 'Теплая архитектурная алюминиевая система ASAŞ Rescara RWT 64 от ведущего турецкого металлургического концерна ASAŞ. Отличная геометрия, надежность и европейские стандарты.',
    related: ['asas-rwt75', 'sistem-wh65', 'sistem-wh75', 'termo-70']
  },
  {
    slug: 'asas-rwt75',
    categorySlug: 'aluminium',
    subcategory: 'ASAŞ Rescara',
    title: 'ASAŞ Rescara RWT 75',
    brand: 'ASAŞ',
    brandCountry: 'Турция 🇹🇷',
    subtitle: 'Премиальная термо-система 75 мм повышенной прочности (Турция)',
    image: pics.asas75,
    specs: [
      'Монтажная глубина рамы: 75 мм',
      'Монтажная глубина створки: 85 мм',
      'Терморазрыв: полиамидные термомосты 34 мм',
      'Толщина стеклопакета: До 56 мм',
      'Теплопроводность: Uf = 1.3 - 1.6 W/m²K',
      'Специальная геометрия дренажных каналов'
    ],
    description: 'Премиальная архитектурная алюминиевая система ASAŞ Rescara RWT 75. Спроектирована для высотных башен, гостиниц и энергопассивных резиденций.',
    related: ['asas-rwt64', 'sistem-wh85', 'sistem-wh75', 'termo-77']
  },

  // ==========================================
  // --- 2. ПВХ ОКНА И ДВЕРИ (PVC) ---
  // ==========================================
  {
    slug: 'deceuninck-6000',
    categorySlug: 'pvc',
    subcategory: 'Deceuninck',
    title: 'Deceuninck 6000',
    brand: 'Deceuninck',
    brandCountry: 'Бельгия 🇧🇪',
    subtitle: 'Надежная 5-камерная ПВХ система 71 мм · 6000 серия (Бельгия)',
    image: pics.deceuninck6000,
    specs: [
      'Монтажная глубина рамы: 71 мм',
      'Количество воздушных камер: 5 камер',
      'Толщина стеклопакета (min/max): 24 мм / 47 мм',
      'Класс профиля: Класс А (толщина стенок 3 мм)',
      'Коэффициент сопротивления теплопередаче: 0.88 м²·°C/Вт',
      'Шумоизоляция: до 40 дБ',
      'Экологическая рецептура EcoLogic без свинца'
    ],
    description: 'Инновационная 5-камерная ПВХ система Deceuninck 6000 (Бельгия). Оптимальная монтажная глубина 71 мм обеспечивает превосходную тепло- и шумоизоляцию для комфортного микроклимата.',
    related: ['deceuninck-7000', 'deceuninck-8000', 'engelberg-7000']
  },
  {
    slug: 'deceuninck-7000',
    categorySlug: 'pvc',
    subcategory: 'Deceuninck',
    title: 'Deceuninck 7000',
    brand: 'Deceuninck',
    brandCountry: 'Бельгия 🇧🇪',
    subtitle: 'Энергоэффективная 5-камерная ПВХ система 71 мм · 7000 серия (Бельгия)',
    image: pics.deceuninck7000,
    specs: [
      'Монтажная глубина рамы: 71 мм',
      'Количество воздушных камер: 5 камер',
      'Толщина стеклопакета (min/max): 24 мм / 47 мм',
      'Контуры уплотнения: 2 контура долговечного TPE-уплотнителя',
      'Высота наплава створки: 20 мм (защита от краевого промерзания)',
      'Класс профиля: Класс А',
      'Коэффициент сопротивления теплопередаче: 0.91 м²·°C/Вт',
      'Шумоизоляция: до 42 дБ'
    ],
    description: 'Энергосберегающая оконная система Deceuninck 7000. Сочетает бельгийскую надежность, увеличенный наплав створки 20 мм для защиты от продуваний и возможность установки теплопакетов до 47 мм.',
    related: ['deceuninck-8000', 'deceuninck-6000', 'engelberg-7600']
  },
  {
    slug: 'deceuninck-8000',
    categorySlug: 'pvc',
    subcategory: 'Deceuninck',
    title: 'Deceuninck 8000',
    brand: 'Deceuninck',
    brandCountry: 'Бельгия 🇧🇪',
    subtitle: 'Флагманская 6-камерная ПВХ система 76 мм с 3 контурами · 8000 серия (Бельгия)',
    image: pics.deceuninck8000,
    specs: [
      'Монтажная глубина рамы: 76 мм',
      'Количество воздушных камер: 6 камер',
      'Толщина стеклопакета (min/max): 24 мм / 50 мм',
      'Контуры уплотнения: 3 контура уплотнения (центральный термо-лепесток)',
      'Класс профиля: Класс А (высший европейский стандарт)',
      'Коэффициент сопротивления теплопередаче: 1.05 м²·°C/Вт',
      'Шумоизоляция: до 44 дБ',
      'Штапик на двух ножках для защиты от выдавливания'
    ],
    description: 'Флагманская премиальная система Deceuninck 8000. 6 воздушных камер, монтажная глубина 76 мм и 3 инновационных контура уплотнения гарантируют эталонную тишину и максимальное энергосбережение.',
    related: ['deceuninck-7000', 'deceuninck-6000', 'engelberg-8000']
  },
  {
    slug: 'engelberg-7000',
    categorySlug: 'pvc',
    subcategory: 'Engelberg',
    title: 'Akfa Engelberg 7000',
    brand: 'Akfa',
    brandCountry: 'Швейцарские технологии 🇨🇭',
    subtitle: '5-камерная ПВХ система 70 мм премиум-класса',
    image: pics.interior,
    specs: [
      'Количество воздушных камер: 5',
      'Монтажная глубина рамы: 70 мм',
      'Толщина стеклопакета (min/max): 24 мм / 32 мм',
      'Толщина армирующего профиля: 1,2 мм',
      'Максимальный вес створки (окно / дверь): 80 кг / 110 кг',
      'Максимальный размер створки (окно): 2000 мм × 1000 мм',
      'Максимальный размер створки (дверь): 2000 мм × 900 мм'
    ],
    description: 'Премиальная 5-камерная система Akfa Engelberg 7000. Обеспечивает безупречный акустический комфорт и защиту от теплопотерь в климате Ташкента.',
    related: ['engelberg-7600', 'engelberg-8000', 'akfa-quattro-6000']
  },
  {
    slug: 'engelberg-7600',
    categorySlug: 'pvc',
    subcategory: 'Engelberg',
    title: 'Akfa Engelberg 7600',
    brand: 'Akfa',
    brandCountry: 'Швейцарские технологии 🇨🇭',
    subtitle: '6-камерная ПВХ система 76 мм · Армирование 2,0 мм',
    image: pics.residence,
    specs: [
      'Количество воздушных камер: 6',
      'Монтажная глубина рамы: 76 мм',
      'Толщина стеклопакета (min/max): 24 мм / 32 мм',
      'Толщина армирующего профиля: 2,0 мм (усиленный металл)',
      'Максимальный вес створки (окна): 100 кг',
      'Максимальный размер створки (окна): 2000 мм × 1000 мм'
    ],
    description: 'Высокопрочная 6-камерная система Akfa Engelberg 7600 с усиленным 2-миллиметровым стальным армированием для максимальной ветровой устойчивости.',
    related: ['engelberg-8000', 'engelberg-7000', 'akfa-trio-6000']
  },
  {
    slug: 'engelberg-8000',
    categorySlug: 'pvc',
    subcategory: 'Engelberg',
    title: 'Akfa Engelberg 8000',
    brand: 'Akfa',
    brandCountry: 'Швейцарские технологии 🇨🇭',
    subtitle: 'Флагманская 6-камерная ПВХ система 80 мм · Армирование 2,0 мм',
    image: pics.interior,
    specs: [
      'Количество воздушных камер: 6',
      'Монтажная глубина рамы: 80 мм',
      'Толщина стеклопакета (min/max): 24 мм / 32 мм',
      'Толщина армирующего профиля: 2,0 мм',
      'Максимальный вес створки (окно): 100 кг',
      'Максимальный размер створки (окно): 2000 мм × 1000 мм'
    ],
    description: 'Флагман модельного ряда Akfa Engelberg — ширина 80 мм, 6 камер и усиленный 2.0 мм металл для максимального энергосбережения и жесткости.',
    related: ['engelberg-7600', 'deceuninck-8000', 'deceuninck-7000']
  },
  {
    slug: 'akfa-trio-6000',
    categorySlug: 'pvc',
    subcategory: 'Akfa Trio',
    title: 'Akfa Trio 6000',
    brand: 'Akfa',
    brandCountry: 'Узбекистан 🇺🇿',
    subtitle: '3-камерная ПВХ система 60 мм для окон и дверей',
    image: pics.tower,
    specs: [
      'Количество воздушных камер: 3',
      'Монтажная глубина рамы: 60 мм',
      'Толщина стеклопакета (min/max): 6 мм / 20 мм',
      'Толщина армирующего профиля: 1,2 мм',
      'Максимальный вес створки (окно / дверь): 80 кг / 110 кг',
      'Применение для окон: до 1850 мм × 900 мм',
      'Системное дверное решение: до 2000 мм × 900 мм'
    ],
    description: 'Классическая 3-камерная ПВХ система Akfa Trio 6000. Практичный выбор для качественного остекления квартир, балконов и административных объектов.',
    related: ['akfa-trio-5800', 'akfa-quattro-6000', 'akfa-quattro-5800']
  },
  {
    slug: 'akfa-quattro-5800',
    categorySlug: 'pvc',
    subcategory: 'Akfa Quattro',
    title: 'Akfa Quattro 5800',
    brand: 'Akfa',
    brandCountry: 'Узбекистан 🇺🇿',
    subtitle: '4-камерная ПВХ система 58 мм',
    image: pics.workshop,
    specs: [
      'Количество воздушных камер: 4',
      'Монтажная глубина рамы: 58 мм',
      'Толщина стеклопакета (min/max): 6 мм / 20 мм',
      'Толщина армирующего профиля: 1,2 мм',
      'Максимальный вес створки (окно / дверь): 60 кг / 100 кг',
      'Максимальный размер створки (окно): 1850 мм × 800 мм',
      'Максимальный размер створки (дверь): 2000 мм × 800 мм'
    ],
    description: 'Энергосберегающая 4-камерная система Akfa Quattro 5800. Отличное соотношение теплотехнических качеств и компактных габаритов профиля.',
    related: ['akfa-quattro-6000', 'akfa-quattro-5200', 'akfa-trio-5800']
  },
  {
    slug: 'akfa-quattro-6000',
    categorySlug: 'pvc',
    subcategory: 'Akfa Quattro',
    title: 'Akfa Quattro 6000',
    brand: 'Akfa',
    brandCountry: 'Узбекистан 🇺🇿',
    subtitle: '4-камерная ПВХ система 60 мм со створкой до 110 кг',
    image: pics.glass,
    specs: [
      'Количество воздушных камер: 4',
      'Монтажная глубина рамы: 60 мм',
      'Толщина стеклопакета (min/max): 6 мм / 20 мм',
      'Толщина армирующего профиля: 1,2 мм',
      'Максимальный вес створки (окно / дверь): 70 кг / 110 кг',
      'Максимальный размер створки (окно): 1750 мм × 850 мм',
      'Максимальный размер створки (дверь): 2000 мм × 900 мм'
    ],
    description: 'Популярная 4-камерная система Akfa Quattro 6000 с монтажной глубиной 60 мм. Обеспечивает улучшенную теплоизоляцию и стабильную геометрию створок.',
    related: ['akfa-quattro-5800', 'akfa-trio-6000', 'engelberg-7000']
  },
  {
    slug: 'akfa-trio-5800',
    categorySlug: 'pvc',
    subcategory: 'Akfa Trio',
    title: 'Akfa Trio 5800',
    brand: 'Akfa',
    brandCountry: 'Узбекистан 🇺🇿',
    subtitle: '3-камерная компактная ПВХ система 58 мм',
    image: pics.tower,
    specs: [
      'Количество воздушных камер: 3',
      'Монтажная глубина рамы: 58 мм',
      'Толщина стеклопакета (min/max): 6 мм / 20 мм',
      'Толщина армирующего профиля: 1,2 мм',
      'Максимальный вес створки (окно / дверь): 70 кг / 100 кг',
      'Максимальный размер створки (окно): 1850 мм × 800 мм',
      'Максимальный размер створки (дверь): 2000 мм × 900 мм'
    ],
    description: 'Компактная и доступная 3-камерная система Akfa Trio 5800 для стандартного остекления оконных проемов, лоджий и хозяйственных блоков.',
    related: ['akfa-trio-6000', 'akfa-quattro-5200']
  },
  {
    slug: 'akfa-quattro-5200',
    categorySlug: 'pvc',
    subcategory: 'Akfa Quattro',
    title: 'Akfa Quattro 5200',
    brand: 'Akfa',
    brandCountry: 'Узбекистан 🇺🇿',
    subtitle: '4-камерная компактная ПВХ система 52 мм',
    image: pics.detail,
    specs: [
      'Количество воздушных камер: 4',
      'Монтажная глубина рамы: 52 мм',
      'Толщина стеклопакета (min/max): 6 мм / 20 мм',
      'Толщина армирующего профиля: 1,2 мм',
      'Максимальный вес створки (окно / дверь): 60 кг / 100 кг',
      'Максимальный размер створки (окно): 1650 мм × 800 мм',
      'Максимальный размер створки (дверь): 1900 мм × 900 мм'
    ],
    description: 'Тонкая 4-камерная система Akfa Quattro 5200 для легких оконных и дверных конструкций с максимальной светопропускной способностью.',
    related: ['akfa-quattro-5800', 'akfa-trio-5800']
  },

  // ==========================================
  // --- 3. РАЗДВИЖНЫЕ СИСТЕМЫ (SLIDING) ---
  // ==========================================
  {
    slug: 'econom-bkg-40',
    categorySlug: 'sliding',
    subcategory: 'BKG Раздвижные',
    title: 'Akfa Econom BKG 40',
    brand: 'Akfa',
    brandCountry: 'Узбекистан 🇺🇿',
    subtitle: 'Экономичная раздвижная алюминиевая система 40 мм',
    image: pics.bkg40,
    specs: [
      'Монтажная глубина рамы: 40 мм',
      'Толщина стеклопакета (min/max): 4 мм / 20 мм',
      'Максимальный вес створки: 60 кг',
      'Максимальный размер створки: 2400 мм × 800 мм',
      'Ширина профиля створки: 40 мм',
      'Ширина видимой части створки: 75 мм',
      'Ширина фурнитурного паза створки: 16,4 мм',
      'Количество направляющих рельсов: 1'
    ],
    description: 'Экономичная раздвижная алюминиевая система Akfa Econom BKG 40 для балконных конструкций, летних террас и легких скользящих створок.',
    related: ['standart-bkh-38', 'standart-bkh-60', 'premium-bkh-65']
  },
  {
    slug: 'standart-bkh-38',
    categorySlug: 'sliding',
    subcategory: 'BKH Раздвижные',
    title: 'Akfa Standart BKH 38',
    brand: 'Akfa',
    brandCountry: 'Узбекистан 🇺🇿',
    subtitle: 'Теплая раздвижная система (2 и 3 рельса) · Термомост 14 мм',
    image: pics.bkh38,
    specs: [
      'Монтажная глубина рамы (2 рельса): 102 мм',
      'Монтажная глубина рамы (3 рельса): 165 мм',
      'Толщина стеклопакета (min/max): 20 мм / 50 мм',
      'Толщина заполнения створки: 16 мм / 28 мм',
      'Ширина профиля створки: 38 мм',
      'Ширина термобарьера: 14 мм',
      'Максимальный вес створки: 100 кг / 160 кг (до 220 кг)'
    ],
    description: 'Теплая раздвижная система Akfa Standart BKH 38 с полиамидным термомостом 14 мм. Доступны конфигурации рамы на 2 и 3 рельса для широких проемов.',
    related: ['standart-bkh-60', 'premium-bkh-65', 'econom-bkg-40']
  },
  {
    slug: 'standart-bkh-60',
    categorySlug: 'sliding',
    subcategory: 'BKH Lift & Slide',
    title: 'Akfa Standart BKH 60',
    brand: 'Akfa',
    brandCountry: 'Узбекистан 🇺🇿',
    subtitle: 'Подъемно-раздвижная портальная система со створками до 400 кг',
    image: pics.bkh60,
    specs: [
      'Монтажная глубина рамы (2 рельса): 145 мм',
      'Толщина стеклопакета (min/max): 20 мм / 32 мм',
      'Максимальный вес створки: 400 кг',
      'Максимальный размер створки: 3300 мм × 2800 мм',
      'Высота подъема системы: 5 – 7 мм'
    ],
    description: 'Мощная подъемно-раздвижная система Akfa Standart BKH 60 для крупногабаритных панорамных выходов на веранды и террасы со створками весом до 400 кг.',
    related: ['premium-bkh-65', 'standart-bkh-38', 'econom-bkg-40']
  },
  {
    slug: 'premium-bkh-65',
    categorySlug: 'sliding',
    subcategory: 'BKH Lift & Slide',
    title: 'Akfa Premium BKH 65',
    brand: 'Akfa',
    brandCountry: 'Узбекистан 🇺🇿',
    subtitle: 'Флагманский теплый подъемно-раздвижной портал · Термомост 34 мм',
    image: pics.bkh65,
    specs: [
      'Монтажная глубина рамы (2 рельса): 148 мм',
      'Монтажная глубина рамы (3 рельса): 232 мм',
      'Толщина стеклопакета (min/max): 20 мм / 50 мм',
      'Максимальный вес створки: 400 кг',
      'Максимальный размер створки: 3300 мм × 2800 мм',
      'Ширина профиля створки: 64 мм',
      'Ширина видимой части створки: 98 мм',
      'Ширина термобарьера рамы: 34 мм'
    ],
    description: 'Флагманская теплая подъемно-раздвижная система Akfa Premium BKH 65 с термомостом 34 мм и возможностью 3-рельсовой рамы глубиной 232 мм.',
    related: ['standart-bkh-60', 'standart-bkh-38', 'econom-bkg-40', 'guillotine-system']
  },
  {
    slug: 'guillotine-system',
    categorySlug: 'sliding',
    subcategory: 'Гильотинные системы',
    title: 'Автоматическая гильотинная система (Guillotine Glass)',
    brand: 'ALL WINDOWS',
    brandCountry: 'Узбекистан / Европа 🇺🇿🇪🇺',
    subtitle: 'Моторизованное вертикально-сдвижное панорамное остекление',
    image: pics.guillotine,
    specs: [
      'Тип открывания: Вертикально-сдвижное (2, 3 или 4 стеклянные панели)',
      'Управление: Электропривод Somfy / Becker с пульта дистанционного управления',
      'Остекление: Закаленное стекло 8-10 мм или стеклопакет 20-28 мм',
      'Максимальная ширина проема: до 4000 мм на одну секцию',
      'Максимальная высота проема: до 3500 мм',
      'Функция стеклянного ограждения: Нижняя панель фиксируется в качестве балюстрады',
      'Профиль: Экструдированный усиленный алюминиевый сплав с порошковой покраской RAL'
    ],
    description: 'Премиальное автоматическое гильотинное остекление с вертикальным движением створок. Идеально подходит для террас, летних веранд ресторанов, кафе, загородных беседок и остекления бассейнов. Стекла бесшумно опускаются и поднимаются по нажатию кнопки, сохраняя панорамный обзор.',
    related: ['premium-bkh-65', 'standart-bkh-60', 'railing-profile']
  },

  // ==========================================
  // --- 4. ФАСАДНЫЕ СИСТЕМЫ (FACADE) ---
  // ==========================================
  {
    slug: 'econom-bkf-48',
    categorySlug: 'facade',
    subcategory: 'BKF Фасады',
    title: 'Akfa Econom BKF 48',
    brand: 'Akfa',
    brandCountry: 'Узбекистан 🇺🇿',
    subtitle: 'Фасадная стоечно-ригельная система 48 мм',
    image: pics.bkf48,
    specs: [
      'Ширина профиля стойки/ригеля: 48 мм',
      'Варианты остекления: Декоративные капоты / Структурное',
      'Толщина стеклопакета (min/max): 6 мм / 38 мм (заполнение 4 мм – 38 мм)',
      'Максимальный вес стеклопакета: до 180 кг',
      'Длина ригеля (min/max): 36 мм / 141 мм',
      'Толщина заполнения (min/max): 4 мм / 38 мм',
      'Термобарьер створки: 27 мм',
      'Длина стойки (min/max): 53 мм / 153 мм'
    ],
    description: 'Универсальная фасадная стоечно-ригельная система Akfa Econom BKF 48. Подходит для витражей, наклонных кровель и фасадов средней этажности.',
    related: ['premium-bkf-50', 'premium-bkf-max']
  },
  {
    slug: 'premium-bkf-50',
    categorySlug: 'facade',
    subcategory: 'BKF Фасады',
    title: 'Akfa Premium BKF 50',
    brand: 'Akfa',
    brandCountry: 'Узбекистан 🇺🇿',
    subtitle: 'Сейсмостойкий фасад 50 мм (до 9 баллов) со стеклопакетом до 62 мм',
    image: pics.bkf50,
    specs: [
      'Ширина профиля стойки/ригеля: 50 мм',
      'Варианты остекления: Декоративные крышки / Структурное',
      'Толщина стеклопакета (min/max): 4 мм / 62 мм',
      'Максимальный вес стеклопакета: до 250 кг',
      'Сейсмостойкость: до 9 баллов',
      'Способ фиксации заполнения: прижимная планка / декоративная крышка'
    ],
    description: 'Премиальная стоечно-ригельная фасадная система Akfa Premium BKF 50 с повышенной сейсмостойкостью до 9 баллов и весом стеклопакета до 250 кг.',
    related: ['premium-bkf-max', 'econom-bkf-48']
  },
  {
    slug: 'premium-bkf-max',
    categorySlug: 'facade',
    subcategory: 'BKF Фасады',
    title: 'Akfa Premium BKF MAX',
    brand: 'Akfa',
    brandCountry: 'Узбекистан 🇺🇿',
    subtitle: 'Минималистичная фасадная система со сверхтонкой стойкой 22 мм',
    image: pics.bkfMax,
    specs: [
      'Ширина стойки и ригеля: 22 мм (сверхтонкий профиль)',
      'Варианты остекления: Декоративные крышки',
      'Толщина стеклопакета (min/max): 24 мм / 42 мм',
      'Максимальный вес заполнения: до 100 кг'
    ],
    description: 'Инновационная ультратонкая фасадная система Akfa Premium BKF MAX со стойкой всего 22 мм для максимальной площади светового проема и легкого внешнего вида.',
    related: ['premium-bkf-50', 'econom-bkf-48']
  },

  // ==========================================
  // --- 5. ОФИСНЫЕ ПЕРЕГОРОДКИ (OFFICE PARTITIONS) ---
  // ==========================================
  {
    slug: 'bko-38',
    categorySlug: 'office-partitions',
    subcategory: 'BKO Перегородки',
    title: 'Akfa BKO 38',
    brand: 'Akfa',
    brandCountry: 'Узбекистан 🇺🇿',
    subtitle: 'Интерьерные офисные перегородки 38 мм с ПВХ прижимом',
    image: pics.bko38,
    specs: [
      'Ширина профиля стойки/ригеля: 38 мм',
      'Варианты остекления: Двойное / Комбинированное',
      'Тип фиксации остекления: ПВХ клипса + декоративная крышка',
      'Толщина заполнения: 4 мм / 6 мм / 8 мм'
    ],
    description: 'Система интерьерных офисных перегородок Akfa BKO 38. Позволяет создавать легкие и элегантные зонирующие конструкции с двойным остеклением и жалюзи.',
    related: ['bko-40', 'aldox']
  },
  {
    slug: 'bko-40',
    categorySlug: 'office-partitions',
    subcategory: 'BKO Перегородки',
    title: 'Akfa BKO 40',
    brand: 'Akfa',
    brandCountry: 'Узбекистан 🇺🇿',
    subtitle: 'Усиленные офисные перегородки 40 мм с кронштейным креплением',
    image: pics.bko40,
    specs: [
      'Ширина профиля стойки/ригеля: 40 мм',
      'Высота профиля стойки: 57 мм',
      'Варианты остекления: Двойное / Комбинированное',
      'Тип фиксации остекления: декоративная крышка',
      'Толщина заполнения: 4 мм / 6 мм / 8 мм',
      'Тип крепления стойки с ригелем: кронштейн (Bracket)'
    ],
    description: 'Усиленная перегородочная система Akfa BKO 40 с высотой стойки 57 мм и кронштейным узлом сопряжения для надежного и безопасного зонирования офисных центров и банков.',
    related: ['bko-38', 'aldox']
  },

  // ==========================================
  // --- 6. ВИТРАЖИ И ПАНОРАМНОЕ ОСТЕКЛЕНИЕ (СПАЙДЕРНЫЕ СИСТЕМЫ) ---
  // ==========================================
  {
    slug: 'spider-glazing',
    categorySlug: 'stained-glass',
    subcategory: 'Спайдерные системы',
    title: 'Спайдерная система остекления (Spider Glass)',
    brand: 'ALL WINDOWS',
    brandCountry: 'Узбекистан 🇺🇿',
    subtitle: 'Точечное крепление стекла кронштейнами из нержавеющей стали',
    image: pics.spiderSystem,
    specs: [
      'Материал кронштейнов-спайдеров: высокопрочная нержавеющая сталь AISI 304 / 316',
      'Конфигурация спайдеров: 1, 2, 3 и 4-лучевые кронштейны',
      'Толщина стекла: закаленный триплекс от 12 мм до 24 мм',
      'Шарнирные рутели: с тефлоновыми демпферами для компенсации ветровых и температурных нагрузок',
      'Максимальная светопрозрачность: отсутствие массивных рамных переплетов',
      'Применение: атриумы, автосалоны, входные группы, торговые и бизнес-центры'
    ],
    description: 'Инновационная технология безрамного спайдерного остекления. Стеклопакеты и закаленный триплекс фиксируются с помощью точечных шарнирных креплений (рутелей) и стальных кронштейнов (спайдеров). Обеспечивает максимальную прозрачность, легкость и современный архитектурный облик здания.',
    related: ['railing-profile', 'guillotine-system']
  },

  // ==========================================
  // --- 7. РОЛЬСТАВНИ И РОЛЛЕТНЫЕ ВОРОТА ---
  // ==========================================
  {
    slug: 'roller-shutters',
    categorySlug: 'rolling-shutters',
    subcategory: 'Рольставни',
    title: 'Рольставни (алюминиевые и сэндвич)',
    brand: 'Akfa',
    brandCountry: 'Узбекистан 🇺🇿',
    subtitle: 'Алюминиевые цельнотянутые и сэндвич с пенонаполнителем 55/77 мм',
    image: pics.rollerShutters,
    specs: [
      'Пенонаполнитель (сэндвич): ширина ламелей 55 мм и 77 мм, вес 0,17 / 0,35 кг/м, срок службы до 80 лет',
      'Алюминиевый цельнотянутый: ширина ламелей 55 мм и 77 мм, вес 0,52 / 0,35 кг/м, срок службы до 80 лет',
      'Пружинно-инерционный привод: грузоподъемность 60–100 кг',
      'Электрический привод: грузоподъемность 15–330 кг с дистанционным управлением',
      'Максимальная высота проема: до 5000 мм',
      'Защита от солнца, шума, пыли и взлома'
    ],
    description: 'Надежные рольставни из алюминиевых профилей двух типов: теплоизолирующие сэндвич-ламели с пенонаполнителем и усиленные цельнотянутые экструдированные ламели для максимальной защиты.',
    related: ['roller-gates', 'termo-70']
  },
  {
    slug: 'roller-gates',
    categorySlug: 'rolling-shutters',
    subcategory: 'Роллетные ворота',
    title: 'Роллетные ворота (алюминиевые и сэндвич)',
    brand: 'Akfa',
    brandCountry: 'Узбекистан 🇺🇿',
    subtitle: 'Автоматические въездные и гаражные роллетные ворота 77 мм',
    image: pics.rollerGates,
    specs: [
      'Пенонаполнитель (сэндвич): ширина ламели 77 мм, срок службы до 80 лет',
      'Алюминиевый усиленный профиль: ширина ламели 77 мм, повышенная ветроустойчивость',
      'Электрический привод с ДУ: грузоподъемность до 330 кг',
      'Аварийная система ручного подъема при отключении электричества',
      'Компактный защитный короб: экономит полезное пространство под потолком',
      'Автоматическая блокировка и защита от препятствий'
    ],
    description: 'Современные рулонные роллетные ворота для гаражей, складов и въездных групп. Прочные 77 мм ламели сворачиваются в компактный короб, не занимая полезную площадь помещения.',
    related: ['roller-shutters', 'railing-profile']
  },

  // ==========================================
  // --- 8. СТЕКЛЯННЫЕ ПЕРИЛА И ОГРАЖДЕНИЯ (BKGF 90) ---
  // ==========================================
  {
    slug: 'railing-profile',
    categorySlug: 'glass-railings',
    subcategory: 'Стеклянные перила',
    title: 'Стеклянные перила BKGF 90',
    brand: 'ALL WINDOWS',
    brandCountry: 'Узбекистан 🇺🇿',
    subtitle: 'Glass railings BKGF 90 look very impressive and modern',
    image: pics.railings,
    specs: [
      'Отличительная особенность: стеклянные ограждения BKGF 90 выглядят очень эффектно и современно',
      'Несущий профиль: высокопрочный алюминиевый зажимной профиль BKGF 90',
      'Заполнение: закаленный безопасный триплекс 12 мм, 16 мм или 20 мм',
      'Способ монтажа: накладной в пол, скрытый в стяжку или боковой к торцу перекрытия',
      'Без вертикальных стоек: сплошное чистое стекло без визуальных барьеров',
      'Высота ограждения: до 1200 мм с расчетной нагрузкой до 1.5 кН/м'
    ],
    description: 'Distinctive feature: Glass railings BKGF 90 look very impressive and modern. Премиальные стеклянные перила и ограждения на базе усиленного алюминиевого профиля BKGF 90. Идеально подходят для лестниц, балконов, открытых террас, атриумов и галерей торговых и частных резиденций.',
    related: ['spider-glazing', 'guillotine-system']
  },

  // ==========================================
  // --- 9. МОСКИТНЫЕ СЕТКИ ---
  // ==========================================
  {
    slug: 'mosquito-inside-frame',
    categorySlug: 'mosquito-nets',
    subcategory: 'Москитные сетки',
    title: 'Сетка внутрирамная',
    brand: 'ALL WINDOWS',
    brandCountry: 'Узбекистан 🇺🇿',
    subtitle: 'Легкая сетка, которую ставят внутрь рамы',
    image: pics.insideFrameNet,
    specs: [
      'Установка: ставится внутрь рамы и прикрепляется специальными поворотными механизмами',
      'Без сверления рамы: сохраняет целостность пластикового или алюминиевого профиля',
      'Плотное прилегание к уплотнителю: исключает щели для проникновения насекомых и пыли',
      'Полотно: сверхпрочное стекловолокно FiberGlass',
      'Легкий уход: снимается за считанные секунды для промывки'
    ],
    description: 'Легкая сетка, которую ставят внутрь рамы и прикрепляют к уплотнителю специальными поворотными механизмами. Идеальное решение, не требующее сверления рамы снаружи.',
    related: ['mosquito-hinged', 'mosquito-frame', 'mosquito-sliding']
  },
  {
    slug: 'mosquito-hinged',
    categorySlug: 'mosquito-nets',
    subcategory: 'Москитные сетки',
    title: 'Сетка на петлях',
    brand: 'ALL WINDOWS',
    brandCountry: 'Узбекистан 🇺🇿',
    subtitle: 'Сетчатая дверь, которая легко открывается и закрывается',
    image: pics.hingedNet,
    specs: [
      'Назначение: сетчатая дверь, которая легко открывается и закрывается',
      'Быстрый съем: при необходимости ее можно снять за несколько минут',
      'Усиленный профиль: мощная алюминиевая рамка с поперечным ребром жесткости',
      'Фурнитура: надежные петли с пружинным самозакрыванием и магнитные защелки',
      'Применение: балконные блоки, террасы, входные двери'
    ],
    description: 'Сетчатая дверь, которая легко открывается и закрывается. При необходимости ее можно снять за несколько минут. Отличный выбор для балконных и террасных дверей.',
    related: ['mosquito-inside-frame', 'mosquito-frame', 'mosquito-sliding']
  },
  {
    slug: 'mosquito-frame',
    categorySlug: 'mosquito-nets',
    subcategory: 'Москитные сетки',
    title: 'Рамочная сетка',
    brand: 'ALL WINDOWS',
    brandCountry: 'Узбекистан 🇺🇿',
    subtitle: 'Популярная конструкция, которая крепится снаружи рам',
    image: pics.frameNet,
    specs: [
      'Популярная конструкция: крепится снаружи рам на надежные Z-образные крепления',
      'Легкость обслуживания: очень легко снять и установить обратно без посторонней помощи',
      'Материал рамки: экструдированный алюминиевый профиль с порошковой покраской',
      'Стойкость к ультрафиолету и перепадам температур',
      'Подходит для всех типов пластиковых и алюминиевых окон'
    ],
    description: 'Популярная конструкция, которая крепится снаружи рам. Очень легко снять и установить обратно без посторонней помощи. Практичный и надежный вариант для любых окон.',
    related: ['mosquito-inside-frame', 'mosquito-hinged', 'mosquito-sliding']
  },
  {
    slug: 'mosquito-sliding',
    categorySlug: 'mosquito-nets',
    subcategory: 'Москитные сетки',
    title: 'Раздвижная москитная сетка',
    brand: 'ALL WINDOWS',
    brandCountry: 'Узбекистан 🇺🇿',
    subtitle: 'Современное и удобное решение для защиты окон от насекомых',
    image: pics.slidingNet,
    specs: [
      'Современное и удобное решение для защиты окон и дверей от насекомых',
      'Складная конструкция: благодаря складной конструкции легко раздвигается и складывается',
      'Экономия места: не требует пространства для распахивания',
      'Плиссированное полотно: повышенная прочность и устойчивость к порывам ветра',
      'Идеально для широких раздвижных систем, порталов и террасных выходов'
    ],
    description: 'Раздвижные москитные сетки – это современное и удобное решение для защиты окон от насекомых. Благодаря складной конструкции они легко раздвигаются и аккуратно собираются гармошкой, экономя пространство.',
    related: ['mosquito-inside-frame', 'mosquito-hinged', 'mosquito-frame']
  }];

function Meta({ 
  title, 
  description, 
  image, 
  type, 
  schema, 
  lang 
}: { 
  title: string; 
  description: string; 
  image?: string; 
  type?: 'website' | 'article' | 'product'; 
  schema?: any; 
  lang?: Lang; 
}) {
  return <SEO title={title} description={description} image={image} type={type} schema={schema} lang={lang} />;
}

function CountUp({ 
  value, 
  duration = 1.8, 
  suffix = '', 
  prefix = '' 
}: { 
  value: number | string; 
  duration?: number; 
  suffix?: string; 
  prefix?: string; 
}) {
  const ref = useRef<HTMLSpanElement>(null);
  const isInView = useInView(ref, { once: true, margin: '-10px' });
  
  let targetNum = typeof value === 'number' ? value : 0;
  let targetPrefix = prefix;
  let targetSuffix = suffix;

  if (typeof value === 'string') {
    const match = value.match(/^([^\d]*)([\d\s,.]+)(.*)$/);
    if (match) {
      targetPrefix = prefix || match[1];
      targetNum = parseFloat(match[2].replace(/\s/g, '').replace(',', '.')) || 0;
      targetSuffix = suffix || match[3];
    }
  }

  const [displayValue, setDisplayValue] = useState(0);
  const prevValueRef = useRef(0);

  useEffect(() => {
    if (!isInView) return;
    const from = prevValueRef.current;
    const to = targetNum;
    const controls = animate(from, to, {
      duration,
      ease: [0.16, 1, 0.3, 1],
      onUpdate(latest) {
        setDisplayValue(latest);
      },
      onComplete() {
        prevValueRef.current = to;
      }
    });
    return () => controls.stop();
  }, [isInView, targetNum, duration]);

  const formattedNum = Math.round(displayValue).toLocaleString('ru-RU');

  return (
    <span ref={ref} className="tabular-nums">
      {targetPrefix}
      {isInView ? formattedNum : '0'}
      {targetSuffix}
    </span>
  );
}

function UniqueSelect({
  label,
  value,
  onChange,
  options,
  placeholder = 'Выберите...',
  testId,
}: {
  label: string;
  value: string;
  onChange: (val: string) => void;
  options: { label: string; value: string; badge?: string; desc?: string }[];
  placeholder?: string;
  testId?: string;
}) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const selectedOption = options.find(o => o.value === value);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div ref={containerRef} className={`relative border-b border-black/20 pb-3 transition-all min-w-0 max-w-full ${open ? 'z-50' : 'z-10'}`}>
      <span className="block text-xs font-semibold text-black/70 mb-1.5 truncate">{label}</span>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex w-full items-center justify-between gap-2 text-left py-1 text-base md:text-lg font-bold text-[#151515] outline-none transition group cursor-pointer min-w-0"
        data-testid={testId}
      >
        <span className="truncate min-w-0 flex-1">{selectedOption ? selectedOption.label : placeholder}</span>
        <ChevronDown size={18} className={`text-black/60 transition-transform duration-200 shrink-0 ${open ? 'rotate-180 text-[#816a3f]' : ''}`} />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 8, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 4, scale: 0.98 }}
            transition={{ duration: 0.15 }}
            className="absolute left-0 right-0 top-[calc(100%+6px)] z-100 overflow-hidden rounded-2xl border border-[#c6a15b]/40 bg-[#171717] shadow-2xl backdrop-blur-xl"
          >
            <div className="custom-scrollbar max-h-64 overflow-y-auto p-2 space-y-1">
              {options.map((opt) => {
                const isSelected = opt.value === value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => {
                      onChange(opt.value);
                      setOpen(false);
                    }}
                    className={`flex w-full items-center justify-between gap-3 rounded-xl px-3.5 py-2.5 text-left text-xs sm:text-sm transition cursor-pointer ${
                      isSelected
                        ? 'bg-[#c6a15b] font-bold text-black shadow-sm'
                        : 'text-white/85 hover:bg-white/10 hover:text-white'
                    }`}
                  >
                    <div className="flex flex-col truncate pr-2">
                      <span className="truncate">{opt.label}</span>
                      {opt.desc && (
                        <span className={`text-[10px] truncate ${isSelected ? 'text-black/70 font-normal' : 'text-white/45'}`}>
                          {opt.desc}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {opt.badge && (
                        <span className={`rounded-full px-2 py-0.5 text-[9px] font-mono ${
                          isSelected ? 'bg-black/20 text-black' : 'bg-white/10 text-white/70'
                        }`}>
                          {opt.badge}
                        </span>
                      )}
                      {isSelected && <Check size={14} className="shrink-0 text-black" />}
                    </div>
                  </button>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function NumberStepperInput({
  label,
  value,
  onChange,
  placeholder = '1000',
  min = 100,
  max = 10000,
  step = 50,
  testId,
}: {
  label: string;
  value: string;
  onChange: (val: string) => void;
  placeholder?: string;
  min?: number;
  max?: number;
  step?: number;
  testId?: string;
}) {
  const handleStep = (delta: number) => {
    const current = parseInt(value, 10) || parseInt(placeholder, 10) || 0;
    const next = Math.max(min, Math.min(max, current + delta));
    onChange(String(next));
  };

  return (
    <div className="border-b border-black/20 pb-2 min-w-0">
      <span className="block text-[11px] sm:text-xs font-semibold text-black/70 mb-1 truncate" title={label}>{label}</span>
      <div className="flex items-center justify-between gap-1 min-w-0">
        <input
          type="number"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          min={min}
          max={max}
          className="w-full min-w-0 bg-transparent py-1 text-sm sm:text-base font-bold text-[#151515] outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
          data-testid={testId}
        />
        <div className="flex flex-col items-center justify-center rounded-lg border border-black/15 bg-black/5 p-0.5 shadow-xs shrink-0">
          <button
            type="button"
            onClick={() => handleStep(step)}
            aria-label="Увеличить"
            className="flex size-4.5 items-center justify-center rounded text-black/60 hover:bg-[#c6a15b] hover:text-black transition-colors cursor-pointer active:scale-90"
          >
            <ChevronUp size={12} strokeWidth={2.5} />
          </button>
          <button
            type="button"
            onClick={() => handleStep(-step)}
            aria-label="Уменьшить"
            className="flex size-4.5 items-center justify-center rounded text-black/60 hover:bg-[#c6a15b] hover:text-black transition-colors cursor-pointer active:scale-90"
          >
            <ChevronDown size={12} strokeWidth={2.5} />
          </button>
        </div>
      </div>
    </div>
  );
}

function Logo() {
  return (
    <Link href="/" className="flex items-center gap-3" data-testid="link-logo">
      <img src="/favicon.webp" alt="ALL WINDOWS" className="size-8 sm:size-9 object-contain shrink-0" />
      <span className="hidden border-l border-[#c6a15b]/40 pl-3 text-[9px] leading-tight tracking-[.2em] text-white/60 sm:block">
        ALL<br />WINDOWS
      </span>
    </Link>
  );
}

function ModalDarkSelect({
  value,
  onChange,
  options,
  placeholder = 'Выберите...',
  testId = 'select-estimate-product',
}: {
  value: string;
  onChange: (val: string) => void;
  options: { label: string; value: string; badge?: string; desc?: string }[];
  placeholder?: string;
  testId?: string;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selected = options.find(o => o.value === value || o.label === value);

  return (
    <div ref={ref} className="relative mt-1">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={`flex w-full items-center justify-between gap-2 rounded-lg border bg-[#0e0e0e] px-3 py-2 text-left text-xs sm:text-sm transition cursor-pointer outline-none ${
          open ? 'border-[#c6a15b] ring-1 ring-[#c6a15b]/40 shadow-[0_0_12px_rgba(198,161,91,0.15)]' : 'border-white/10 hover:border-white/25'
        }`}
        data-testid={testId}
      >
        <span className={`truncate ${selected ? 'text-white font-medium' : 'text-white/40'}`}>
          {selected ? selected.label : placeholder}
        </span>
        <ChevronDown
          size={15}
          className={`shrink-0 text-white/50 transition-transform duration-200 ${open ? 'rotate-180 text-[#d4b16a]' : ''}`}
        />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -4, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -4, scale: 0.98 }}
            transition={{ duration: 0.15 }}
            className="absolute left-0 right-0 top-[calc(100%+4px)] z-50 overflow-hidden rounded-xl border border-[#c6a15b]/40 bg-[#161616] shadow-[0_12px_32px_rgba(0,0,0,0.9)] backdrop-blur-xl"
          >
            <div className="custom-scrollbar max-h-48 overflow-y-auto p-1.5 space-y-0.5">
              {options.map((opt) => {
                const isSelected = value === opt.value || value === opt.label;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => {
                      onChange(opt.value);
                      setOpen(false);
                    }}
                    className={`flex w-full items-center justify-between rounded-lg px-2.5 py-1.5 text-left text-xs transition cursor-pointer ${
                      isSelected
                        ? 'bg-[#c6a15b] font-semibold text-black shadow-xs'
                        : 'text-white/85 hover:bg-[#c6a15b]/15 hover:text-[#d4b16a]'
                    }`}
                  >
                    <div className="flex flex-col truncate pr-1">
                      <span className="truncate">{opt.label}</span>
                      {opt.desc && (
                        <span className={`text-[9.5px] truncate ${isSelected ? 'text-black/75' : 'text-white/40'}`}>
                          {opt.desc}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0 ml-1">
                      {opt.badge && (
                        <span className={`rounded-full px-1.5 py-0.5 text-[8.5px] font-mono ${
                          isSelected ? 'bg-black/20 text-black' : 'bg-white/10 text-white/60'
                        }`}>
                          {opt.badge}
                        </span>
                      )}
                      {isSelected && <Check size={13} className="shrink-0 text-black" />}
                    </div>
                  </button>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function ModalNumberInput({
  label,
  value,
  onChange,
  placeholder = '1800',
  step = 50,
  min = 100,
  max = 10000,
  testId,
}: {
  label: string;
  value: string;
  onChange: (val: string) => void;
  placeholder?: string;
  step?: number;
  min?: number;
  max?: number;
  testId?: string;
}) {
  const handleStep = (delta: number) => {
    const current = parseInt(value, 10) || parseInt(placeholder, 10) || 0;
    const next = Math.max(min, Math.min(max, current + delta));
    onChange(String(next));
  };

  return (
    <div>
      <span className="block text-[11px] font-medium text-white/60 mb-1 truncate">{label}</span>
      <div className="flex items-center justify-between gap-1.5 rounded-lg border border-white/10 bg-[#0e0e0e] px-2.5 py-1.5 transition focus-within:border-[#c6a15b]">
        <input
          type="number"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          min={min}
          max={max}
          className="w-full min-w-0 bg-transparent text-xs sm:text-sm font-semibold text-white outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none placeholder:text-white/30"
          data-testid={testId}
        />
        <div className="flex items-center gap-0.5 shrink-0">
          <button
            type="button"
            onClick={() => handleStep(-step)}
            aria-label="Уменьшить"
            className="flex size-5 items-center justify-center rounded bg-white/5 text-white/60 hover:bg-[#c6a15b] hover:text-black transition-colors cursor-pointer active:scale-95"
          >
            <ChevronDown size={11} strokeWidth={2.5} />
          </button>
          <button
            type="button"
            onClick={() => handleStep(step)}
            aria-label="Увеличить"
            className="flex size-5 items-center justify-center rounded bg-white/5 text-white/60 hover:bg-[#c6a15b] hover:text-black transition-colors cursor-pointer active:scale-95"
          >
            <ChevronUp size={11} strokeWidth={2.5} />
          </button>
        </div>
      </div>
    </div>
  );
}

const getModalGlassOptions = (lang: Lang) => [
  { label: lang === 'uz' ? 'Ikki kamerali energiya tejovchi Low-E' : 'Двухкамерный энергосберегающий Low-E', value: 'Двухкамерный энергосберегающий', desc: lang === 'uz' ? 'Turar-joylar uchun maksimal issiqlik izolyatsiyasi' : 'Максимальная теплоизоляция для жилья' },
  { label: lang === 'uz' ? 'Ikki kamerali ko‘p funksiyali' : 'Двухкамерный мультифункциональный', value: 'Двухкамерный мультифункциональный', desc: lang === 'uz' ? 'Yozda quyoshdan himoya va qishda issiqlikni saqlash' : 'Защита от солнца летом и тепла зимой' },
  { label: lang === 'uz' ? 'Bir kamerali standart' : 'Однокамерный стандартный', value: 'Однокамерный стандартный', desc: lang === 'uz' ? 'Verandalar, tamburlar va terassalar uchun' : 'Для веранд, тамбуров и террас' },
  { label: lang === 'uz' ? 'Xavfsiz tripleks (laminatsiyalangan)' : 'Триплекс безопасный (ламинированный)', value: 'Триплекс безопасный (ламинированный)', desc: lang === 'uz' ? 'Yuqori darajadagi xavfsizlik va himoya' : 'Повышенная безопасность и защита' },
  { label: lang === 'uz' ? 'Toblangan shisha (Tempered)' : 'Закаленное стекло (Tempered)', value: 'Закаленное стекло', desc: lang === 'uz' ? 'To‘siqlar va vitrajlar uchun' : 'Для перегородок и витражей' },
];

function resolveInitialItem(initialProduct: string, initialColor: string): FormProductItem {
  const defaultColor = initialColor || windowColors[0].name;
  if (!initialProduct) {
    return createDefaultFormItem('aluminium-windows', defaultColor);
  }

  const query = initialProduct.toLowerCase().trim();
  
  // 1. Поиск по продуктам FORM_CATEGORIES
  for (const cat of FORM_CATEGORIES) {
    const pMatch = cat.products.find(p => 
      p.value.toLowerCase() === query ||
      p.labelRu.toLowerCase().includes(query) ||
      p.labelUz.toLowerCase().includes(query)
    );
    if (pMatch) {
      return {
        id: `item_init_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`,
        categorySlug: cat.slug,
        productSlug: pMatch.value,
        customProductTitle: '',
        color: defaultColor,
        glass: 'Двухкамерный энергосберегающий',
        width: cat.defaultWidth,
        height: cat.defaultHeight,
        quantity: '1'
      };
    }
  }

  // 2. Прямое совпадение со slug категории
  const directCat = FORM_CATEGORIES.find(c => c.slug.toLowerCase() === query);
  if (directCat) {
    return createDefaultFormItem(directCat.slug, defaultColor);
  }

  // 3. Совпадение по ключевым словам
  if (query.includes('двер') || query.includes('eshik') || query.includes('door')) {
    if (query.includes('пвх') || query.includes('pvc')) {
      return createDefaultFormItem('pvc-doors', defaultColor);
    }
    return createDefaultFormItem('aluminium-doors', defaultColor);
  }
  if (query.includes('пвх') || query.includes('pvc') || query.includes('deceuninck') || query.includes('engelberg') || query.includes('trio') || query.includes('quattro')) {
    return createDefaultFormItem('pvc-windows', defaultColor);
  }
  if (query.includes('sliding') || query.includes('раздвиж') || query.includes('surma') || query.includes('гильотин') || query.includes('bkh')) {
    return createDefaultFormItem('sliding', defaultColor);
  }
  if (query.includes('facade') || query.includes('фасад') || query.includes('bkf')) {
    return createDefaultFormItem('facade', defaultColor);
  }
  if (query.includes('partition') || query.includes('перегород') || query.includes('to‘siq') || query.includes('bko')) {
    return createDefaultFormItem('office-partitions', defaultColor);
  }
  if (query.includes('stained') || query.includes('витраж') || query.includes('спайдер') || query.includes('spider')) {
    return createDefaultFormItem('stained-glass', defaultColor);
  }
  if (query.includes('shutter') || query.includes('рольставн') || query.includes('роллет') || query.includes('darvoza')) {
    return createDefaultFormItem('rolling-shutters', defaultColor);
  }
  if (query.includes('railing') || query.includes('перил') || query.includes('огражден') || query.includes('panjara')) {
    return createDefaultFormItem('glass-railings', defaultColor);
  }
  if (query.includes('net') || query.includes('сетк') || query.includes('chivin')) {
    return createDefaultFormItem('mosquito-nets', defaultColor);
  }

  // Если это произвольный текст (например, из карточки проекта)
  const def = createDefaultFormItem('aluminium-windows', defaultColor);
  def.productSlug = CUSTOM_PRODUCT_VALUE;
  def.customProductTitle = initialProduct;
  return def;
}

function prepareLeadSubmission(items: FormProductItem[], name: string, phone: string, comment: string, source: string, lang: Lang) {
  const leadItems: LeadProductItem[] = items.map(item => {
    const catConfig = getFormCategory(item.categorySlug);
    const catTitle = lang === 'uz' ? catConfig.titleUz : catConfig.titleRu;
    let prodTitle = '';
    if (item.productSlug === CUSTOM_PRODUCT_VALUE) {
      prodTitle = item.customProductTitle.trim() || (lang === 'uz' ? 'O‘z varianti' : 'Индивидуальный вариант');
    } else {
      const pOption = catConfig.products.find(p => p.value === item.productSlug);
      prodTitle = (lang === 'uz' ? pOption?.labelUz : pOption?.labelRu) || pOption?.labelRu || item.productSlug;
    }
    return {
      categoryTitle: catTitle,
      productTitle: prodTitle,
      color: item.color,
      glass: item.glass,
      width: item.width,
      height: item.height,
      quantity: item.quantity || '1',
    };
  });

  const primaryItem = leadItems[0] || {
    categoryTitle: 'Алюминиевые окна',
    productTitle: 'Akfa TERMO 77',
    color: 'Антрацит матовый',
    glass: 'Двухкамерный энергосберегающий',
    width: '1800',
    height: '1400',
    quantity: '1'
  };
  const isMulti = leadItems.length > 1;

  const categorySummary = isMulti
    ? leadItems.map((it, idx) => `${idx + 1}) ${it.categoryTitle}`).join(', ')
    : primaryItem.categoryTitle;

  const productSummary = isMulti
    ? leadItems.map((it, idx) => `${idx + 1}) ${it.productTitle} [${it.width}×${it.height} мм, ${it.quantity || 1} шт]`).join('; ')
    : (primaryItem.quantity && parseInt(String(primaryItem.quantity), 10) > 1 
        ? `${primaryItem.productTitle} (${primaryItem.quantity} шт)` 
        : primaryItem.productTitle);

  const dimensionsSummary = isMulti
    ? leadItems.map((it, idx) => `${idx + 1}) ${it.width}×${it.height} мм`).join('; ')
    : `${primaryItem.width} × ${primaryItem.height} мм`;

  const colorSummary = isMulti
    ? Array.from(new Set(leadItems.map(it => it.color).filter(Boolean))).join(', ')
    : primaryItem.color;

  const glassSummary = isMulti
    ? Array.from(new Set(leadItems.map(it => it.glass).filter(Boolean))).join(', ')
    : primaryItem.glass;

  return {
    name: name.trim(),
    phone: phone.trim(),
    items: leadItems,
    categoryTitle: categorySummary,
    productTitle: productSummary,
    dimensions: dimensionsSummary,
    width: primaryItem.width,
    height: primaryItem.height,
    color: colorSummary,
    glass: glassSummary,
    comment: comment.trim(),
    source,
  };
}

function EstimateModal({
  open,
  onClose,
  lang = 'ru',
  initialProduct = '',
  initialColor = '',
}: {
  open: boolean;
  onClose: () => void;
  lang?: Lang;
  initialProduct?: string;
  initialColor?: string;
}) {
  const t = copy(lang);
  const modalGlassOptions = getModalGlassOptions(lang);
  const categoryOptions = getFormCategoryOptions(lang);

  const [status, setStatus] = useState<'form' | 'loading' | 'done'>('form');
  const [items, setItems] = useState<FormProductItem[]>(() => [resolveInitialItem(initialProduct, initialColor)]);
  const [name, setName] = useState(''); 
  const [phone, setPhone] = useState('+998 '); 
  const [comment, setComment] = useState('');
  const [modalError, setModalError] = useState<string | null>(null);

  useEffect(() => { 
    if (open) { 
      setStatus('form'); 
      setPhone('+998 '); 
      setModalError(null);
      setItems([resolveInitialItem(initialProduct, initialColor)]);
    } 
  }, [open, initialProduct, initialColor]);

  const updateItem = (id: string, updates: Partial<FormProductItem>) => {
    setItems(prev => prev.map(item => {
      if (item.id !== id) return item;
      const next = { ...item, ...updates };
      if (updates.categorySlug && updates.categorySlug !== item.categorySlug) {
        const catConfig = getFormCategory(updates.categorySlug);
        next.productSlug = catConfig.products[0]?.value || '';
        next.customProductTitle = '';
        next.width = catConfig.defaultWidth;
        next.height = catConfig.defaultHeight;
      }
      return next;
    }));
  };

  const addItem = () => {
    const last = items[items.length - 1];
    const newItem = createDefaultFormItem('aluminium-windows', last?.color || initialColor || windowColors[0].name);
    setItems(prev => [...prev, newItem]);
  };

  const removeItem = (id: string) => {
    if (items.length <= 1) return;
    setItems(prev => prev.filter(item => item.id !== id));
  };

  const submit = async (e: FormEvent) => { 
    e.preventDefault(); 
    setModalError(null);

    if (!name.trim()) {
      setModalError(lang === 'uz' ? 'Iltimos, ismingizni kiriting' : 'Пожалуйста, укажите ваше имя');
      return;
    }
    const purePhone = phone.replace(/\D/g, '');
    if (purePhone.length < 9) {
      setModalError(lang === 'uz' ? "Iltimos, to'liq telefon raqamingizni kiriting (+998 ...)" : 'Пожалуйста, введите полный номер телефона (+998...)');
      return;
    }

    setStatus('loading'); 
    try {
      const payload = prepareLeadSubmission(
        items,
        name,
        phone,
        comment,
        'Модальное окно расчёта (Сайт)',
        lang
      );
      await submitLead(payload);
      setStatus('done');
    } catch {
      setModalError(lang === 'uz' ? 'Yuborishda xatolik yuz berdi. Iltimos, qayta urinib ko‘ring.' : 'Ошибка отправки. Пожалуйста, попробуйте еще раз.');
      setStatus('form');
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-3 sm:p-4 backdrop-blur-md overflow-y-auto" 
          initial={{ opacity: 0 }} 
          animate={{ opacity: 1 }} 
          exit={{ opacity: 0 }} 
          onClick={onClose}
        >
          <motion.div 
            className="relative my-auto w-full max-w-2xl rounded-2xl border border-[#c6a15b]/35 bg-[#151515] p-4.5 sm:p-6 shadow-2xl max-h-[92svh] overflow-y-auto custom-scrollbar" 
            initial={{ opacity: 0, y: 18, scale: 0.98 }} 
            animate={{ opacity: 1, y: 0, scale: 1 }} 
            exit={{ opacity: 0, y: 14, scale: 0.98 }} 
            transition={{ duration: 0.2 }}
            onClick={e => e.stopPropagation()}
          >
            <button onClick={onClose} className="absolute right-4 top-4 text-white/50 hover:text-[#d4b16a] transition cursor-pointer p-1 rounded-lg hover:bg-white/5" aria-label={lang === 'uz' ? 'Yopish' : 'Закрыть'} data-testid="button-close-modal">
              <X size={18} />
            </button>
            {status === 'done' ? (
              <div className="py-6 text-center">
                <div className="mx-auto mb-4 grid size-14 place-items-center rounded-full bg-[#c6a15b] text-[#101010] shadow-lg">
                  <Check size={26} />
                </div>
                <h2 className="font-display text-2xl sm:text-3xl font-extrabold text-white">
                  {t.calculator.successTitle}
                </h2>
                <p className="mt-2.5 text-xs sm:text-sm text-white/70 max-w-md mx-auto leading-relaxed">
                  {lang === 'uz' 
                    ? `Rahmat, ${name}. Arizangiz qabul qilindi. ALL WINDOWS mutaxassisi 24 soat ichida siz bilan bog‘lanadi.` 
                    : `Спасибо, ${name}. Ваша заявка успешно принята. Специалист ALL WINDOWS свяжется с вами в течение 24 часов.`}
                </p>

                {/* Сводка по позициям */}
                <div className="mt-4 max-w-md mx-auto rounded-xl border border-white/10 bg-[#0e0e0e] p-3 text-left">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-[#d4b16a] mb-2">
                    {lang === 'uz' ? `Tanlangan konstruksiyalar (${items.length} ta):` : `Выбранные конструкции (${items.length}):`}
                  </p>
                  <div className="space-y-1.5 text-xs text-white/80 max-h-40 overflow-y-auto custom-scrollbar pr-1">
                    {items.map((it, idx) => {
                      const cat = getFormCategory(it.categorySlug);
                      const catTitle = lang === 'uz' ? cat.titleUz : cat.titleRu;
                      let prodName = '';
                      if (it.productSlug === CUSTOM_PRODUCT_VALUE) {
                        prodName = it.customProductTitle || (lang === 'uz' ? 'O‘z varianti' : 'Индивидуально');
                      } else {
                        const pOpt = cat.products.find(p => p.value === it.productSlug);
                        prodName = (lang === 'uz' ? pOpt?.labelUz : pOpt?.labelRu) || it.productSlug;
                      }
                      return (
                        <div key={it.id} className="border-b border-white/5 pb-1.5 last:border-0 last:pb-0 flex items-start justify-between gap-2">
                          <div>
                            <span className="font-semibold text-white">{idx + 1}. {catTitle}</span>
                            <span className="text-white/50 text-[11px] block">{prodName} • {it.width} × {it.height} мм ({it.quantity || 1} {t.calculator.quantityUnit})</span>
                          </div>
                          <span className="text-[10px] text-white/40 shrink-0">{it.color}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <button onClick={onClose} className="mt-6 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-6 py-2.5 text-xs sm:text-sm font-semibold text-white hover:bg-white/20 transition cursor-pointer" data-testid="button-modal-done">
                  {t.actions.backToHome}
                </button>
              </div>
            ) : (
              <>
                <p className="mb-1 text-[9px] uppercase tracking-[.25em] text-[#d4b16a]">ALL WINDOWS / 01</p>
                <h2 className="font-display text-2xl sm:text-3xl font-extrabold leading-tight text-white">
                  {t.calculator.title}
                </h2>
                <p className="mt-1 text-xs leading-relaxed text-white/55">
                  {t.calculator.subtitle}
                </p>

                <form onSubmit={submit} className="mt-4 space-y-3">
                  {/* Список добавленных конструкций */}
                  <div className="space-y-3">
                    {items.map((item, idx) => (
                      <div key={item.id} className="rounded-xl border border-white/10 bg-[#0e0e0e] p-3 sm:p-3.5 space-y-2.5 relative">
                        {/* Заголовок карточки изделия */}
                        <div className="flex items-center justify-between border-b border-white/10 pb-2">
                          <div className="flex items-center gap-2">
                            <span className="grid size-5 place-items-center rounded-full bg-[#c6a15b]/20 text-[#d4b16a] text-[10px] font-bold">
                              {idx + 1}
                            </span>
                            <span className="text-xs font-bold text-white">
                              {t.calculator.itemHeading(idx + 1)}
                            </span>
                            <span className="rounded-full bg-white/5 px-2 py-0.5 text-[9.5px] text-white/50 hidden sm:inline-block">
                              {lang === 'uz' ? getFormCategory(item.categorySlug).titleUz : getFormCategory(item.categorySlug).titleRu}
                            </span>
                          </div>

                          {items.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeItem(item.id)}
                              className="flex items-center gap-1 text-[11px] font-medium text-red-400 hover:text-red-300 transition cursor-pointer px-2 py-0.5 rounded hover:bg-red-500/10"
                              aria-label={t.calculator.removeProduct}
                              title={t.calculator.removeProduct}
                            >
                              <Trash2 size={12} />
                              <span>{t.calculator.removeProduct}</span>
                            </button>
                          )}
                        </div>

                        {/* Селекторы категории и системы */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                          <div>
                            <span className="block text-[11px] font-medium text-white/60 mb-1">
                              {t.calculator.stepCategory}
                            </span>
                            <ModalDarkSelect
                              value={item.categorySlug}
                              onChange={(val) => updateItem(item.id, { categorySlug: val })}
                              options={categoryOptions}
                              placeholder={lang === 'uz' ? 'Toifani tanlang' : 'Выберите категорию'}
                              testId={`select-estimate-category-${idx}`}
                            />
                          </div>

                          <div>
                            <span className="block text-[11px] font-medium text-white/60 mb-1">
                              {t.calculator.stepProduct}
                            </span>
                            <ModalDarkSelect
                              value={item.productSlug}
                              onChange={(val) => updateItem(item.id, { productSlug: val })}
                              options={getFormProductOptions(item.categorySlug, lang)}
                              placeholder={lang === 'uz' ? 'Tizimni tanlang...' : 'Выберите систему...'}
                              testId={`select-estimate-product-${idx}`}
                            />
                          </div>

                          {/* Поле ввода для своего варианта */}
                          {item.productSlug === CUSTOM_PRODUCT_VALUE && (
                            <div className="sm:col-span-2">
                              <label className="block text-[11px] font-medium text-[#d4b16a] mb-1">
                                {lang === 'uz' ? 'O‘zingizning tizimingiz / istaklaringiz:' : 'Название или параметры вашей системы:'}
                                <input
                                  type="text"
                                  value={item.customProductTitle}
                                  onChange={(e) => updateItem(item.id, { customProductTitle: e.target.value })}
                                  placeholder={t.calculator.customProductPlaceholder}
                                  className="mt-1 w-full rounded-lg border border-[#c6a15b]/40 bg-[#141414] px-3 py-2 text-xs sm:text-sm text-white outline-none focus:border-[#c6a15b] placeholder:text-white/30 shadow-inner"
                                  required
                                />
                              </label>
                            </div>
                          )}

                          {/* Выбор цвета */}
                          <div className="sm:col-span-2 rounded-lg border border-white/5 bg-[#121212] p-2.5">
                            <ColorPicker
                              selectedColorId={windowColors.find(c => c.name === item.color || c.id === item.color)?.id || windowColors[0].id}
                              onSelectColor={(c) => updateItem(item.id, { color: c.name })}
                              maxVisible={7}
                              variant="dark"
                              lang={lang}
                            />
                          </div>

                          {/* Выбор стеклопакета */}
                          <div className="sm:col-span-2">
                            <span className="block text-[11px] font-medium text-white/60 mb-1">
                              {t.calculator.stepGlass}
                            </span>
                            <ModalDarkSelect
                              value={item.glass}
                              onChange={(val) => updateItem(item.id, { glass: val })}
                              options={modalGlassOptions}
                              placeholder={lang === 'uz' ? 'Shisha paketi turi' : 'Тип стеклопакета'}
                              testId={`select-estimate-glass-${idx}`}
                            />
                          </div>

                          {/* Размеры и количество (на отдельной строке во всю ширину) */}
                          <div className="sm:col-span-2 grid grid-cols-3 gap-2 sm:gap-3">
                            <ModalNumberInput
                              label={t.calculator.width}
                              value={item.width}
                              onChange={(val) => updateItem(item.id, { width: val })}
                              placeholder="1800"
                              step={50}
                              testId={`input-estimate-width-${idx}`}
                            />
                            <ModalNumberInput
                              label={t.calculator.height}
                              value={item.height}
                              onChange={(val) => updateItem(item.id, { height: val })}
                              placeholder="1400"
                              step={50}
                              testId={`input-estimate-height-${idx}`}
                            />
                            <ModalNumberInput
                              label={`${t.calculator.quantityLabel} (${t.calculator.quantityUnit})`}
                              value={item.quantity || '1'}
                              onChange={(val) => updateItem(item.id, { quantity: val })}
                              placeholder="1"
                              step={1}
                              min={1}
                              max={100}
                              testId={`input-estimate-qty-${idx}`}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Кнопка добавления ещё одной конструкции */}
                  <button
                    type="button"
                    onClick={addItem}
                    className="flex w-full items-center justify-center gap-2 rounded-xl border border-dashed border-[#c6a15b]/50 bg-[#c6a15b]/10 hover:bg-[#c6a15b]/15 py-2.5 text-xs font-bold text-[#d4b16a] transition cursor-pointer active:scale-[0.99]"
                    data-testid="button-add-item-modal"
                  >
                    <Plus size={15} />
                    <span>{t.calculator.addAnotherProduct}</span>
                  </button>

                  {/* Контактные данные */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 pt-1">
                    <div>
                      <label className="block text-[11px] font-medium text-white/60">
                        {t.calculator.nameLabel}
                        <input 
                          required 
                          value={name} 
                          onChange={e => setName(e.target.value)} 
                          placeholder={t.calculator.namePlaceholder} 
                          className="mt-1 w-full rounded-lg border border-white/10 bg-[#0e0e0e] px-3 py-2 text-xs sm:text-sm text-white outline-none transition focus:border-[#c6a15b] placeholder:text-white/30" 
                          data-testid="input-estimate-name" 
                        />
                      </label>
                    </div>

                    <div>
                      <label className="block text-[11px] font-medium text-white/60">
                        {t.calculator.phoneLabel}
                        <input 
                          required 
                          type="tel"
                          value={phone} 
                          onChange={e => setPhone(formatPhoneNumber(e.target.value))} 
                          onKeyDown={handlePhoneKeyDown}
                          placeholder="+998 (__) ___-__-__" 
                          className="mt-1 w-full rounded-lg border border-white/10 bg-[#0e0e0e] px-3 py-2 text-xs sm:text-sm text-white outline-none transition focus:border-[#c6a15b] placeholder:text-white/30" 
                          data-testid="input-estimate-phone" 
                        />
                      </label>
                    </div>
                  </div>

                  <div>
                    <label className="block text-[11px] font-medium text-white/60">
                      {t.calculator.commentLabel}
                      <textarea 
                        value={comment} 
                        onChange={e => setComment(e.target.value)} 
                        placeholder={t.calculator.commentPlaceholder} 
                        rows={2} 
                        className="mt-1 w-full resize-none rounded-lg border border-white/10 bg-[#0e0e0e] px-3 py-2 text-xs sm:text-sm text-white outline-none transition focus:border-[#c6a15b] placeholder:text-white/30" 
                        data-testid="textarea-estimate-comment" 
                      />
                    </label>
                  </div>

                  {modalError && (
                    <div className="rounded-lg bg-red-500/10 border border-red-500/30 px-3 py-2 text-xs text-red-400 font-medium">
                      {modalError}
                    </div>
                  )}

                  <div className="pt-1">
                    <button 
                      disabled={status === 'loading'} 
                      type="submit" 
                      className="gold-gradient flex w-full items-center justify-center gap-2 rounded-full py-2.5 sm:py-3 text-xs sm:text-sm font-bold text-[#121212] transition hover:brightness-105 cursor-pointer disabled:opacity-60 shadow-lg" 
                      data-testid="button-submit-estimate"
                    >
                      {status === 'loading' ? t.actions.submitting : <>{t.actions.submitEstimate} <ArrowRight size={15} /></>}
                    </button>
                    <p className="mt-2 text-center text-[10.5px] text-white/40">
                      {t.actions.freeEstimateNotice}
                    </p>
                  </div>
                </form>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function Header({ lang, setLang, onEstimate }: { lang: Lang; setLang: (l: Lang) => void; onEstimate: () => void }) {
  const [open, setOpen] = useState(false); 
  const [scrolled, setScrolled] = useState(false); 
  const [location] = useLocation(); 
  const t = copy(lang);

  useEffect(() => { 
    const f = () => setScrolled(window.scrollY > 24); 
    window.addEventListener('scroll', f); 
    return () => window.removeEventListener('scroll', f); 
  }, []);

  const navItems = [
    { href: '/products', label: t.nav.products },
    { href: '/projects', label: t.nav.projects },
    { href: '/about', label: t.nav.about },
    { href: '/services', label: t.nav.services },
    { href: '/contacts', label: t.nav.contacts },
  ];

  return (
    <header className={`fixed inset-x-0 top-0 z-40 transition-all ${scrolled ? 'border-b border-white/10 bg-[#0d0d0d]/90 backdrop-blur-xl' : 'bg-linear-to-b from-black/60 to-transparent'}`}>
      <div className="mx-auto flex max-w-360 items-center justify-between px-6 sm:px-8 py-5 lg:px-12 xl:px-14">
        <Logo />
        <nav className="hidden items-center gap-7 xl:flex">
          {navItems.map((item) => { 
            const isActive = location === item.href || (item.href === '/products' && location.startsWith('/products'));
            return (
              <Link key={item.href} href={item.href} className={`text-[11px] uppercase tracking-widest transition hover:text-[#d4b16a] ${isActive ? 'text-[#d4b16a]' : 'text-white/65'}`} data-testid={`link-nav-${item.href.slice(1)}`}>
                {item.label}
              </Link>
            ); 
          })}
        </nav>
        <div className="flex items-center gap-3">
          <a href="tel:+998888001800" className="hidden items-center gap-2 text-xs text-white/75 lg:flex" data-testid="link-header-phone">
            <Phone size={14} className="text-[#d4b16a]" /> +998 88 800 18 00
          </a>
          <button onClick={onEstimate} className="hidden h-8 items-center justify-center rounded-full bg-[#c6a15b] px-3.5 text-[9.5px] font-bold uppercase tracking-wider text-[#111] transition hover:bg-[#e0c083] cursor-pointer lg:inline-flex" data-testid="button-header-estimate">
            {t.actions.estimate}
          </button>
          <div className="relative flex h-8 items-center rounded-full border border-white/15 bg-black/40 p-0.5 text-[10px] font-bold tracking-wider backdrop-blur-sm">
            {(['ru', 'uz'] as const).map((l) => (
              <button
                key={l}
                onClick={() => setLang(l)}
                className={`relative z-10 flex h-full items-center justify-center rounded-full px-2.5 uppercase transition-colors duration-200 cursor-pointer ${
                  lang === l ? 'text-[#111]' : 'text-white/50 hover:text-white/80'
                }`}
                data-testid={`button-language-${l}`}
              >
                {lang === l && (
                  <motion.div
                    layoutId="activeLangIndicator"
                    className="absolute inset-0 rounded-full bg-[#c6a15b] shadow-sm"
                    transition={{
                      type: 'spring',
                      stiffness: 500,
                      damping: 32,
                    }}
                  />
                )}
                <span className="relative z-10">{l.toUpperCase()}</span>
              </button>
            ))}
          </div>
          <button onClick={() => setOpen(!open)} className="grid size-10 place-items-center rounded-full border border-white/15 xl:hidden" aria-label={lang === 'uz' ? 'Menyu' : 'Меню'} data-testid="button-mobile-menu">
            {open ? <X size={19} /> : <Menu size={19} />}
          </button>
        </div>
      </div>
      <AnimatePresence>
        {open && (
          <motion.nav initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="border-t border-white/10 bg-[#111]/95 px-5 pb-6 pt-3 xl:hidden">
            {navItems.map((item) => (
              <Link onClick={() => setOpen(false)} key={item.href} href={item.href} className="block border-b border-white/10 py-3.5 text-sm text-white/80" data-testid={`link-mobile-nav-${item.href.slice(1)}`}>
                {item.label}<ArrowUpRight className="float-right text-[#c6a15b]" size={16} />
              </Link>
            ))}
            <a href="tel:+998888001800" onClick={() => setOpen(false)} className="flex items-center justify-between border-b border-white/10 py-3.5 text-sm font-semibold text-[#d4b16a]" data-testid="link-mobile-phone">
              <span className="flex items-center gap-2.5"><Phone size={15} /> +998 88 800 18 00</span>
              <span className="text-[10px] uppercase tracking-wider text-white/50">{t.actions.call}</span>
            </a>
            <button onClick={() => { setOpen(false); onEstimate(); }} className="mt-5 w-full rounded-full bg-[#c6a15b] py-3 text-sm font-bold text-black" data-testid="button-mobile-estimate">
              {t.actions.estimate}
            </button>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}

function Footer({ lang, onEstimate }: { lang: Lang; onEstimate: () => void }) {
  const t = copy(lang);
  return (
    <footer className="border-t border-white/10 bg-[#0a0a0a]">
      <div className="mx-auto grid max-w-360 gap-12 px-6 sm:px-8 py-16 md:grid-cols-4 lg:px-12 xl:px-14">
        <div className="md:col-span-1">
          <Logo />
          <p className="mt-6 max-w-52.5 text-sm leading-6 text-white/45">
            {lang === 'uz' 
              ? 'Toshkentning zamonaviy arxitekturasi uchun deraza, eshik va fasad tizimlari.'
              : 'Оконные, дверные и фасадные системы для современной архитектуры Ташкента.'}
          </p>
          <p className="mt-9 text-[10px] uppercase tracking-[.2em] text-[#c6a15b]">
            {lang === 'uz' ? 'Bizning yo‘nalish' : 'Направление'}
          </p>
        </div>
        <div>
          <p className="mb-5 text-[10px] uppercase tracking-[.2em] text-[#c6a15b]">{t.footer.navTitle}</p>
          <div className="space-y-3 text-sm text-white/55">
            <Link href="/products" className="block hover:text-white" data-testid="link-footer-products">{t.nav.products}</Link>
            <Link href="/projects" className="block hover:text-white" data-testid="link-footer-projects">{t.nav.projects}</Link>
            <Link href="/about" className="block hover:text-white" data-testid="link-footer-about">{t.nav.about}</Link>
            <Link href="/services" className="block hover:text-white" data-testid="link-footer-services">{t.nav.services}</Link>
          </div>
        </div>
        <div>
          <p className="mb-5 text-[10px] uppercase tracking-[.2em] text-[#c6a15b]">{t.footer.contactsTitle}</p>
          <div className="space-y-3 text-sm text-white/55">
            <a href="tel:+998888001800" className="block hover:text-white" data-testid="link-footer-phone">+998 88 800 18 00</a>
            <p className="whitespace-pre-line">{t.footer.address}</p>
            <p className="whitespace-pre-line">{t.footer.hours}</p>
          </div>
        </div>
        <div>
          <p className="mb-5 text-[10px] uppercase tracking-[.2em] text-[#c6a15b]">{t.footer.touchTitle}</p>
          <p className="mb-5 text-sm leading-6 text-white/55">{t.footer.touchDesc}</p>
          <button onClick={onEstimate} className="flex items-center gap-2 text-sm text-[#d4b16a] cursor-pointer" data-testid="button-footer-estimate">
            {t.footer.leaveRequest} <ArrowRight size={15} />
          </button>
          <div className="mt-7 flex gap-4 text-white/45">
            <a href="https://instagram.com/all_windows" aria-label="Instagram" data-testid="link-footer-instagram"><Instagram size={18} /></a>
            <a href="https://t.me/all_windows" aria-label="Telegram" data-testid="link-footer-telegram"><Send size={18} /></a>
          </div>
        </div>
      </div>
      <div className="mx-auto flex max-w-360 justify-between border-t border-white/10 px-6 sm:px-8 py-5 text-[10px] text-white/30 lg:px-12 xl:px-14">
        <span>{t.footer.copyright}</span>
        <span>{t.footer.tagline}</span>
      </div>
    </footer>
  );
}

function Shell({ children, lang, setLang, onEstimate }: { children: ReactNode; lang: Lang; setLang: (l: Lang) => void; onEstimate: (p?: string) => void }) {
  const [location] = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);

  return (
    <div className="site-noise min-h-dvh bg-[#0d0d0d]">
      <Header lang={lang} setLang={setLang} onEstimate={() => onEstimate()} />
      {children}
      <Footer lang={lang} onEstimate={() => onEstimate()} />
      <div className="fixed bottom-4 right-4 z-30 flex flex-col gap-2 md:hidden">
        <a href="tel:+998888001800" className="grid size-12 place-items-center rounded-full bg-[#c6a15b] text-[#111] shadow-xl" aria-label={lang === 'uz' ? 'Qo‘ng‘iroq qilish' : 'Позвонить'} data-testid="button-floating-call"><Phone size={18} /></a>
        <a href="https://t.me/all_windows" className="grid size-12 place-items-center rounded-full border border-[#c6a15b] bg-[#141414] text-[#d4b16a]" aria-label="Telegram" data-testid="button-floating-telegram"><Send size={18} /></a>
      </div>
    </div>
  );
}

function SectionIntro({ eyebrow, title, text, light = false }: { eyebrow: string; title: string; text?: string; light?: boolean }) {
  return (
    <div className="mb-12 max-w-3xl">
      <div className="mb-5 flex items-center gap-4">
        <span className={`text-[10px] uppercase tracking-[.28em] ${light ? 'text-[#816a3f]' : 'text-[#d4b16a]'}`}>{eyebrow}</span>
        <span className={`h-px w-16 ${light ? 'bg-[#816a3f]' : 'bg-[#c6a15b]'}`} />
      </div>
      <h2 className={`font-display text-4xl font-extrabold leading-[1.08] tracking-[-.04em] md:text-6xl ${light ? 'text-[#151515]' : 'text-white'}`}>{title}</h2>
      {text && <p className={`mt-5 max-w-xl text-base leading-7 ${light ? 'text-black/55' : 'text-white/50'}`}>{text}</p>}
    </div>
  );
}

function Home({ onEstimate, lang }: { onEstimate: (p?: string) => void; lang: Lang }) {
  const t = copy(lang); 
  const [slide, setSlide] = useState(0);
  const [homeProjectModal, setHomeProjectModal] = useState<Project | null>(null);
  const [randomProjects, setRandomProjects] = useState<Project[]>(() => {
    const pool = projects.filter(p => !p.isTextOnly && p.images && p.images.length > 0);
    return [...pool].sort(() => Math.random() - 0.5).slice(0, 3);
  });

  const [homeItems, setHomeItems] = useState<FormProductItem[]>(() => [createDefaultFormItem('aluminium-windows', windowColors[0].name)]);
  const [formName, setFormName] = useState('');
  const [formPhone, setFormPhone] = useState('+998 ');
  const [formComment, setFormComment] = useState('');
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'done'>('idle');
  const [homeFormError, setHomeFormError] = useState<string | null>(null);

  const updateHomeItem = (id: string, updates: Partial<FormProductItem>) => {
    setHomeItems(prev => prev.map(item => {
      if (item.id !== id) return item;
      const next = { ...item, ...updates };
      if (updates.categorySlug && updates.categorySlug !== item.categorySlug) {
        const catConfig = getFormCategory(updates.categorySlug);
        next.productSlug = catConfig.products[0]?.value || '';
        next.customProductTitle = '';
        next.width = catConfig.defaultWidth;
        next.height = catConfig.defaultHeight;
      }
      return next;
    }));
  };

  const addHomeItem = () => {
    const last = homeItems[homeItems.length - 1];
    const newItem = createDefaultFormItem('aluminium-windows', last?.color || windowColors[0].name);
    setHomeItems(prev => [...prev, newItem]);
  };

  const removeHomeItem = (id: string) => {
    if (homeItems.length <= 1) return;
    setHomeItems(prev => prev.filter(item => item.id !== id));
  };

  const handleHomeSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setHomeFormError(null);

    if (!formName.trim()) {
      setHomeFormError(lang === 'uz' ? 'Iltimos, ismingizni kiriting' : 'Пожалуйста, укажите ваше имя');
      return;
    }
    const purePhone = formPhone.replace(/\D/g, '');
    if (purePhone.length < 9) {
      setHomeFormError(lang === 'uz' ? "Iltimos, to'liq telefon raqamingizni kiriting (+998 ...)" : 'Пожалуйста, введите полный номер телефона (+998...)');
      return;
    }

    setFormStatus('loading');
    try {
      const payload = prepareLeadSubmission(
        homeItems,
        formName,
        formPhone,
        formComment,
        'Главная страница (Форма расчёта)',
        lang
      );
      await submitLead(payload);
      setFormStatus('done');
    } catch {
      setHomeFormError(lang === 'uz' ? 'Yuborishda xatolik yuz berdi. Iltimos, qayta urinib ko‘ring.' : 'Ошибка отправки. Пожалуйста, попробуйте еще раз.');
      setFormStatus('idle');
    }
  };

  const categoryOptions = getFormCategoryOptions(lang);
  const glassOptions = getModalGlassOptions(lang);

  const testimonials = lang === 'uz' ? [
    { n: 'Aleksey S.', o: 'Aminar House', q: 'Jamoa fasad ishlari jadvaliga to‘liq rioya qildi va barcha tutashish nuqtalarini sifatli bajardi.' }, 
    { n: 'Malika R.', o: 'Xususiy uy, Toshkent', q: 'Panoramali derazalar aynan loyihadagidek chiqdi. O‘lchovchi mutaxassisning ishini alohida ta’kidlayman.' }, 
    { n: 'Ilya K.', o: 'Fazo Residence', q: 'Shaffof hisob-kitob, xususiy ishlab chiqarish va obyektda kechikishlarsiz montaj.' }
  ] : [
    { n: 'Алексей С.', o: 'Aminar House', q: 'Команда выдержала график фасадных работ и аккуратно прошла все узлы примыкания.' }, 
    { n: 'Малика Р.', o: 'Частный дом, Ташкент', q: 'Панорамные окна получились именно такими, как в проекте. Отдельно отмечу работу замерщика.' }, 
    { n: 'Илья К.', o: 'Fazo Residence', q: 'Понятный расчёт, собственное производство и монтаж без простоев на объекте.' }
  ];

  return (
    <>
      <SEO 
        title={lang === 'uz' ? 'ALL WINDOWS — Toshkentda deraza, eshik va fasad tizimlari' : 'ALL WINDOWS — Производство и монтаж окон, дверей и фасадов в Ташкенте'} 
        description={lang === 'uz' ? 'Toshkentda alyuminiy va PVX deraza, eshik, fasad va surma tizimlarini ishlab chiqarish hamda professional montaj qilish. 10 yilgacha kafolat.' : 'Премиальное производство и профессиональный монтаж алюминиевых и ПВХ окон, дверей, витражей, фасадных систем и рольставней в Ташкенте с гарантией качества.'} 
        canonicalPath="/"
        image="/og.webp"
        imageAlt="ALL WINDOWS — Окна, двери и фасады в Ташкенте"
        lang={lang}
      />
      <main>
        <section className="relative flex h-svh max-h-svh min-h-135 flex-col justify-end overflow-hidden border-b border-white/10 pb-6 sm:pb-10 md:pb-14 pt-20 sm:pt-24 md:pt-28">
          <img src={pics.hero} alt="Панорамное остекление современного дома" className="absolute inset-0 size-full object-cover object-center opacity-65" />
          <div className="absolute inset-0 bg-linear-to-t from-[#0d0d0d] via-[#0d0d0d]/40 to-black/20" />
          <div className="relative mx-auto w-full max-w-360 px-6 sm:px-8 lg:px-12 xl:px-14">
            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: .7 }} className="max-w-4xl">
              <p className="mb-3 sm:mb-5 text-[10px] uppercase tracking-[.3em] text-[#e0c083]">
                {lang === 'uz' ? 'Toshkentda ishlab chiqarish · 2019 yildan buyon' : 'Производство в Ташкенте · с 2019 года'}
              </p>
              <h1 className="font-display text-4xl sm:text-6xl md:text-7xl lg:text-8xl font-extrabold leading-[.95] tracking-[-.06em] text-white">
                {lang === 'uz' ? <>Yorug‘lik.<br /><span className="text-[#d4b16a]">Aniq shakl.</span></> : <>Свет.<br /><span className="text-[#d4b16a]">Точная форма.</span></>}
              </h1>
              <p className="mt-4 sm:mt-6 max-w-xl text-xs sm:text-base md:text-lg leading-relaxed text-white/65">
                {t.hero.subtitle}
              </p>
              <div className="mt-5 sm:mt-8 flex flex-wrap gap-3">
                <button onClick={() => onEstimate()} className="gold-gradient flex items-center gap-3 rounded-full px-6 py-3.5 text-sm font-bold text-[#111] cursor-pointer" data-testid="button-hero-estimate">
                  {t.actions.estimate} <ArrowRight size={17} />
                </button>
                <a href="tel:+998888001800" className="flex items-center gap-3 rounded-full border border-white/20 px-6 py-3.5 text-sm font-semibold text-white transition hover:border-[#c6a15b] hover:text-[#d4b16a]" data-testid="link-hero-phone">
                  <Phone size={16} /> {t.actions.call}
                </a>
              </div>
            </motion.div>
            <div className="mt-6 sm:mt-10 flex items-center gap-3 text-[10px] uppercase tracking-[.18em] text-white/40">
              <span>01</span>
              <span className="h-px w-24 bg-white/25" />
              <span>{t.hero.bottomTag}</span>
            </div>
          </div>
        </section>

        <section className="border-b border-white/10 bg-[#111]">
          <div className="mx-auto max-w-360 px-6 sm:px-8 lg:px-12 xl:px-14">
            <div className="grid grid-cols-2 divide-x divide-y divide-white/10 md:grid-cols-4 md:divide-y-0">
              <Benefit icon={<Factory />} title={t.benefits.production.title} text={t.benefits.production.text} />
              <Benefit icon={<FileCheck2 />} title={t.benefits.profiles.title} text={t.benefits.profiles.text} />
              <Benefit icon={<Wrench />} title={t.benefits.installation.title} text={t.benefits.installation.text} />
              <Benefit icon={<ShieldCheck />} title={t.benefits.warranty.title} text={t.benefits.warranty.text} />
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-360 px-6 sm:px-8 py-24 lg:px-12 xl:px-14">
          <SectionIntro eyebrow={t.homeProducts.eyebrow} title={t.homeProducts.title} text={t.homeProducts.text} />
          <div className="grid gap-3 md:grid-cols-12">
            {categories.slice(0, 6).map((p, i) => (
              <Link key={p.slug} href={`/products/${p.slug}`} className={`group image-zoom relative min-h-67.5 overflow-hidden rounded-xl border border-white/10 ${i === 0 || i === 3 || i === 4 ? 'md:col-span-7' : 'md:col-span-5'}`} data-testid={`card-home-product-${p.slug}`}>
                <img src={p.image} alt={p.title} className="absolute inset-0 size-full object-cover opacity-70" />
                <div className="absolute inset-0 bg-linear-to-t from-black via-black/15 to-transparent" />
                <div className="relative flex h-full min-h-67.5 flex-col justify-end p-6">
                  <span className="mb-2 text-[10px] uppercase tracking-[.2em] text-[#d4b16a]">0{i + 1} / AW system</span>
                  <h3 className="font-display text-2xl font-extrabold">{t.categories[p.slug]?.title || p.title}</h3>
                  <span className="mt-4 flex items-center gap-2 text-xs text-white/55 transition group-hover:text-[#d4b16a]">
                    {t.actions.details} <ArrowUpRight size={14} />
                  </span>
                </div>
              </Link>
            ))}
          </div>
          <Link href="/products" className="mt-8 inline-flex items-center gap-3 border-b border-[#c6a15b] pb-2 text-sm text-[#d4b16a]" data-testid="link-all-products">
            {t.actions.allProducts} <ArrowRight size={15} />
          </Link>
        </section>

        <section className="bg-[#d9c8a3] px-4 sm:px-8 py-10 sm:py-14 md:py-16 text-[#151515] lg:px-12 xl:px-14 flex flex-col justify-center min-h-svh relative z-20 overflow-x-clip">
          <div className="mx-auto w-full max-w-360">
            <div className="mb-6 md:mb-8 max-w-3xl">
              <div className="mb-2 flex items-center gap-4">
                <span className="text-[10px] uppercase tracking-[.28em] text-[#816a3f]">{t.calculator.eyebrow}</span>
                <span className="h-px w-16 bg-[#816a3f]" />
              </div>
              <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-extrabold leading-[1.08] tracking-[-.04em] text-[#151515]">
                {t.calculator.title}
              </h2>
              <p className="mt-2.5 max-w-xl text-xs sm:text-sm leading-6 text-black/60">
                {t.calculator.subtitle}
              </p>
            </div>

            {formStatus === 'done' ? (
              <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col items-center justify-center rounded-2xl bg-[#171717] p-8 md:p-12 text-center text-white max-w-xl mx-auto shadow-2xl border border-[#c6a15b]/30 my-4">
                <div className="mb-4 grid size-14 place-items-center rounded-full bg-[#c6a15b] text-black">
                  <Check size={28} />
                </div>
                <h3 className="font-display text-2xl md:text-3xl font-bold">
                  {t.calculator.successTitle}
                </h3>
                <p className="mt-3 text-xs sm:text-sm text-white/70 max-w-md leading-relaxed">
                  {lang === 'uz'
                    ? `Rahmat, ${formName}. Arizangiz qabul qilindi. ALL WINDOWS mutaxassisi 24 soat ichida siz bilan bog‘lanadi.`
                    : `Спасибо, ${formName}. Ваша заявка успешно принята. Специалист ALL WINDOWS свяжется с вами в течение 24 часов.`}
                </p>

                {/* Сводка по позициям */}
                <div className="mt-5 w-full max-w-md rounded-xl border border-white/10 bg-[#0e0e0e] p-3.5 text-left">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-[#d4b16a] mb-2">
                    {lang === 'uz' ? `Tanlangan konstruksiyalar (${homeItems.length} ta):` : `Выбранные конструкции (${homeItems.length}):`}
                  </p>
                  <div className="space-y-2 text-xs text-white/80 max-h-48 overflow-y-auto custom-scrollbar pr-1">
                    {homeItems.map((it, idx) => {
                      const cat = getFormCategory(it.categorySlug);
                      const catTitle = lang === 'uz' ? cat.titleUz : cat.titleRu;
                      let prodName = '';
                      if (it.productSlug === CUSTOM_PRODUCT_VALUE) {
                        prodName = it.customProductTitle || (lang === 'uz' ? 'O‘z varianti' : 'Индивидуально');
                      } else {
                        const pOpt = cat.products.find(p => p.value === it.productSlug);
                        prodName = (lang === 'uz' ? pOpt?.labelUz : pOpt?.labelRu) || it.productSlug;
                      }
                      return (
                        <div key={it.id} className="border-b border-white/5 pb-2 last:border-0 last:pb-0 flex items-start justify-between gap-2">
                          <div>
                            <span className="font-semibold text-white">{idx + 1}. {catTitle}</span>
                            <span className="text-white/55 text-[11px] block">{prodName} • {it.width} × {it.height} мм ({it.quantity || 1} {t.calculator.quantityUnit})</span>
                          </div>
                          <span className="text-[10px] text-white/40 shrink-0">{it.color}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => {
                    setFormStatus('idle');
                    setFormName('');
                    setFormPhone('+998 ');
                    setFormComment('');
                    setHomeItems([createDefaultFormItem('aluminium-windows', windowColors[0].name)]);
                  }}
                  className="mt-6 rounded-full border border-white/20 bg-white/10 px-6 py-2.5 text-xs font-semibold text-white hover:bg-white/20 transition cursor-pointer"
                >
                  {t.calculator.newRequestBtn}
                </button>
              </motion.div>
            ) : (
              <form onSubmit={handleHomeSubmit} className="relative z-30 w-full max-w-4xl min-w-0">
                {/* Список позиций / изделий */}
                <div className="space-y-4 mb-5">
                  {homeItems.map((item, idx) => (
                    <div key={item.id} className="rounded-2xl border border-black/10 bg-white/80 backdrop-blur-md p-3.5 sm:p-5 shadow-xs space-y-4 w-full max-w-full overflow-hidden min-w-0">
                      {/* Заголовок карточки позиции */}
                      <div className="flex items-center justify-between border-b border-black/10 pb-2.5">
                        <div className="flex items-center gap-2">
                          <span className="grid size-6 place-items-center rounded-full bg-[#151515] text-[#d4b16a] text-xs font-bold">
                            {idx + 1}
                          </span>
                          <span className="text-sm font-extrabold text-[#151515]">
                            {t.calculator.itemHeading(idx + 1)}
                          </span>
                          <span className="rounded-full bg-black/5 px-2.5 py-0.5 text-[10px] font-semibold text-black/60 hidden sm:inline-block">
                            {lang === 'uz' ? getFormCategory(item.categorySlug).titleUz : getFormCategory(item.categorySlug).titleRu}
                          </span>
                        </div>

                        {homeItems.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeHomeItem(item.id)}
                            className="flex items-center gap-1.5 text-xs font-semibold text-red-600 hover:text-red-700 transition cursor-pointer px-2.5 py-1 rounded-lg hover:bg-red-50"
                            aria-label={t.calculator.removeProduct}
                            title={t.calculator.removeProduct}
                          >
                            <Trash2 size={13} />
                            <span>{t.calculator.removeProduct}</span>
                          </button>
                        )}
                      </div>

                      <div className="grid gap-x-6 gap-y-3.5 sm:grid-cols-2 min-w-0">
                        <div className="min-w-0">
                          <UniqueSelect
                            label={t.calculator.stepCategory}
                            value={item.categorySlug}
                            onChange={(val) => updateHomeItem(item.id, { categorySlug: val })}
                            options={categoryOptions}
                            placeholder={lang === 'uz' ? 'Toifani tanlang' : 'Выберите категорию'}
                            testId={`select-home-category-${idx}`}
                          />
                        </div>

                        <div className="min-w-0">
                          <UniqueSelect
                            label={t.calculator.stepProduct}
                            value={item.productSlug}
                            onChange={(val) => updateHomeItem(item.id, { productSlug: val })}
                            options={getFormProductOptions(item.categorySlug, lang)}
                            placeholder={lang === 'uz' ? 'Tizimni tanlang...' : 'Выберите систему...'}
                            testId={`select-home-product-${idx}`}
                          />
                        </div>

                        {/* Поле для ввода своего варианта */}
                        {item.productSlug === CUSTOM_PRODUCT_VALUE && (
                          <div className="sm:col-span-2 border-b border-black/20 pb-2 min-w-0">
                            <label className="block text-xs font-bold text-black/80">
                              {lang === 'uz' ? 'O‘zingizning tizimingiz / parametrlar:' : 'Название или параметры вашей системы:'}
                              <input
                                type="text"
                                required
                                value={item.customProductTitle}
                                onChange={(e) => updateHomeItem(item.id, { customProductTitle: e.target.value })}
                                placeholder={t.calculator.customProductPlaceholder}
                                className="mt-1 block w-full bg-transparent py-1 text-sm font-bold text-[#151515] outline-none placeholder:text-black/40 border-b border-black/20 focus:border-black min-w-0"
                              />
                            </label>
                          </div>
                        )}

                        <div className="sm:col-span-2 border-b border-black/15 pb-3 min-w-0">
                          <ColorPicker
                            selectedColorId={windowColors.find(c => c.name === item.color || c.id === item.color)?.id || windowColors[0].id}
                            onSelectColor={(c) => updateHomeItem(item.id, { color: c.name })}
                            maxVisible={8}
                            variant="light"
                            lang={lang}
                          />
                        </div>

                        <div className="sm:col-span-2 min-w-0">
                          <UniqueSelect
                            label={t.calculator.stepGlass}
                            value={item.glass}
                            onChange={(val) => updateHomeItem(item.id, { glass: val })}
                            options={glassOptions}
                            placeholder={lang === 'uz' ? 'Shisha paketi turi' : 'Тип стеклопакета'}
                            testId={`select-home-glass-${idx}`}
                          />
                        </div>

                        {/* Размеры и количество (на отдельной строке во всю ширину) */}
                        <div className="sm:col-span-2 grid grid-cols-2 sm:grid-cols-3 gap-2.5 sm:gap-4 min-w-0">
                          <NumberStepperInput
                            label={t.calculator.width}
                            value={item.width}
                            onChange={(val) => updateHomeItem(item.id, { width: val })}
                            placeholder="1800"
                            step={50}
                            testId={`input-home-width-${idx}`}
                          />
                          <NumberStepperInput
                            label={t.calculator.height}
                            value={item.height}
                            onChange={(val) => updateHomeItem(item.id, { height: val })}
                            placeholder="1400"
                            step={50}
                            testId={`input-home-height-${idx}`}
                          />
                          <div className="col-span-2 sm:col-span-1 min-w-0">
                            <NumberStepperInput
                              label={`${t.calculator.quantityLabel} (${t.calculator.quantityUnit})`}
                              value={item.quantity || '1'}
                              onChange={(val) => updateHomeItem(item.id, { quantity: val })}
                              placeholder="1"
                              step={1}
                              min={1}
                              max={100}
                              testId={`input-home-qty-${idx}`}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Кнопка добавления ещё одной конструкции */}
                <div className="mb-6">
                  <button
                    type="button"
                    onClick={addHomeItem}
                    className="inline-flex items-center gap-2 rounded-xl border-2 border-dashed border-black/30 hover:border-black/60 bg-black/5 hover:bg-black/10 px-5 py-2.5 text-xs sm:text-sm font-bold text-[#151515] transition cursor-pointer active:scale-95"
                    data-testid="button-add-item-home"
                  >
                    <Plus size={16} />
                    <span>{t.calculator.addAnotherProduct}</span>
                  </button>
                </div>

                {/* Контактные данные */}
                <div className="grid gap-x-8 gap-y-3.5 sm:grid-cols-2 min-w-0">
                  <label className="border-b border-black/20 pb-2 text-xs font-semibold min-w-0 block">
                    {t.calculator.nameLabel}
                    <input 
                      required 
                      value={formName} 
                      onChange={e => setFormName(e.target.value)} 
                      placeholder={t.calculator.namePlaceholder}
                      className="mt-1 block w-full min-w-0 bg-transparent py-1 text-base font-bold outline-none placeholder:text-black/40" 
                      data-testid="input-home-name" 
                    />
                  </label>

                  <label className="border-b border-black/20 pb-2 text-xs font-semibold min-w-0 block">
                    {t.calculator.phoneLabel}
                    <input 
                      required 
                      type="tel"
                      value={formPhone} 
                      onChange={e => setFormPhone(formatPhoneNumber(e.target.value))} 
                      onKeyDown={handlePhoneKeyDown}
                      placeholder="+998 (__) ___-__-__"
                      className="mt-1 block w-full min-w-0 bg-transparent py-1 text-base font-bold outline-none placeholder:text-black/40" 
                      data-testid="input-home-phone" 
                    />
                  </label>

                  <label className="border-b border-black/20 pb-2 text-xs font-semibold sm:col-span-2 min-w-0 block">
                    {t.calculator.commentLabel}
                    <input 
                      value={formComment} 
                      onChange={e => setFormComment(e.target.value)} 
                      placeholder={t.calculator.commentPlaceholder}
                      className="mt-1 block w-full min-w-0 bg-transparent py-1 text-sm font-medium outline-none placeholder:text-black/40" 
                      data-testid="input-home-comment" 
                    />
                  </label>
                </div>

                {homeFormError && (
                  <div className="mt-4 rounded-xl bg-red-500/10 border border-red-500/30 px-4 py-2.5 text-xs font-semibold text-red-600">
                    {homeFormError}
                  </div>
                )}

                <div className="mt-6 flex flex-wrap items-center gap-4">
                  <button 
                    disabled={formStatus === 'loading'} 
                    type="submit" 
                    className="gold-gradient flex items-center justify-center gap-3 rounded-full px-8 py-3.5 text-sm font-bold text-black shadow-lg hover:shadow-xl transition cursor-pointer disabled:opacity-60" 
                    data-testid="button-home-submit"
                  >
                    {formStatus === 'loading' ? t.actions.submitting : <>{t.calculator.submitBtn} <ArrowRight size={17} /></>}
                  </button>
                  <span className="text-xs text-black/55 font-medium">
                    {lang === 'uz' ? '24 soat ichida javob beramiz va smeta hisoblaymiz' : 'Ответим и рассчитаем в течение 24 часов'}
                  </span>
                </div>
              </form>
            )}
          </div>
        </section>

        <section className="mx-auto max-w-360 px-6 sm:px-8 py-20 lg:px-12 xl:px-14">
          <SectionIntro eyebrow={t.projectsSection.eyebrow} title={t.projectsSection.title} text={t.projectsSection.subtitle} />
          <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-3">
            {randomProjects.map((p) => (
              <div 
                key={p.slug} 
                onClick={() => setHomeProjectModal(p)} 
                className="group image-zoom relative h-64 sm:h-72 overflow-hidden rounded-xl cursor-pointer border border-white/10 bg-[#141414] transition duration-300 hover:border-[#c6a15b]/60 hover:shadow-xl" 
                data-testid={`card-home-project-${p.slug}`}
              >
                <img 
                  src={p.image} 
                  alt={p.title} 
                  className="absolute inset-0 size-full object-cover transition-transform duration-500 group-hover:scale-105" 
                />
                <div className="absolute inset-0 bg-linear-to-t from-black/95 via-black/35 to-transparent" />
                
                <div className="absolute top-3 right-3 rounded-full bg-black/70 backdrop-blur-md px-2.5 py-1 text-[11px] text-white/90 border border-white/15 flex items-center gap-1.5 shadow-lg">
                  <Images size={11} className="text-[#d4b16a]" /> {t.projectsSection.photosCount(p.images.length)}
                </div>
                
                <div className="relative flex h-full flex-col justify-end p-5">
                  <p className="text-[9px] uppercase tracking-[.22em] text-[#d4b16a]">{p.type} · {p.city}</p>
                  <h3 className="mt-1 font-display text-lg font-bold text-white group-hover:text-[#d4b16a] transition">{p.title}</h3>
                  <span className="mt-2.5 inline-flex items-center gap-1.5 text-xs font-semibold text-[#d4b16a]">
                    {t.actions.openGallery} <Maximize2 size={12} />
                  </span>
                </div>
              </div>
            ))}
          </div>
          <Link href="/projects" className="mt-8 inline-flex items-center gap-3 border-b border-[#c6a15b] pb-2 text-sm text-[#d4b16a]" data-testid="link-home-projects">
            {t.actions.allProjects} <ArrowRight size={15} />
          </Link>
          <ProjectGalleryModal project={homeProjectModal} onClose={() => setHomeProjectModal(null)} />
        </section>

        <section className="border-y border-white/10 bg-[#111]">
          <div className="mx-auto max-w-360 px-6 sm:px-8 lg:px-12 xl:px-14">
            <div className="grid grid-cols-2 divide-x divide-white/10 md:grid-cols-4">
              <Stat value="98+" label={lang === 'uz' ? 'amalga oshirilgan loyihalar' : 'реализованных проектов'} />
              <Stat value="2 400+" label={lang === 'uz' ? 'bizga ishongan mijozlar' : 'клиентов доверили нам окна'} />
              <Stat value="7" label={lang === 'uz' ? 'yil ishlab chiqarishda (2019 yildan)' : 'лет в производстве (с 2019 года)'} />
              <Stat value="24 ч" label={lang === 'uz' ? 'o‘rtacha ariza javobi' : 'средний ответ по заявке'} />
            </div>
          </div>
        </section>

        <section className="mx-auto grid max-w-360 gap-12 px-6 sm:px-8 py-24 lg:grid-cols-[.8fr_1.2fr] lg:px-12 xl:px-14">
          <SectionIntro 
            eyebrow={lang === 'uz' ? '04 / Jarayon' : '04 / Процесс'} 
            title={lang === 'uz' ? 'G‘oyadan to aniq o‘lchamgacha' : 'От идеи до точного проёма'} 
            text={lang === 'uz' ? 'Barcha bosqichlarda yagona mas’ul pudratchi — muhandislik yechimidan to so‘nggi sozlashgacha.' : 'Один ответственный подрядчик на всех этапах — от инженерного решения до последней регулировки.'} 
          />
          <div className="divide-y divide-white/10">
            {(lang === 'uz' 
              ? ['Ariza va konsultatsiya', 'Bepul o‘lchov', 'Hisoblash va shartnoma', 'Toshkentda ishlab chiqarish', 'Montaj va kafolat']
              : ['Заявка и консультация', 'Бесплатный замер', 'Расчёт и договор', 'Производство в Ташкенте', 'Монтаж и гарантия']
            ).map((s, i) => (
              <div key={s} className="flex items-center justify-between py-5">
                <div className="flex items-center gap-5">
                  <span className="font-mono text-xs text-[#c6a15b]">0{i + 1}</span>
                  <span className="font-display text-xl font-bold">{s}</span>
                </div>
                <ArrowUpRight size={18} className="text-white/35" />
              </div>
            ))}
          </div>
        </section>

        <section className="bg-[#151515] px-6 sm:px-8 py-24 lg:px-12 xl:px-14">
          <div className="mx-auto max-w-225">
            <SectionIntro 
              eyebrow={lang === 'uz' ? '05 / Sharhlar' : '05 / Отзывы'} 
              title={lang === 'uz' ? 'Ushbu makonlarda yashayotgan va ishlayotgan insonlar fikri' : 'Говорят люди, которые живут и работают в этих пространствах'} 
            />
            <div className="relative min-h-45">
              <AnimatePresence mode="wait">
                <motion.div key={slide} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                  <p className="max-w-2xl font-display text-2xl font-semibold leading-snug md:text-4xl">«{testimonials[slide].q}»</p>
                  <div className="mt-8 flex items-center gap-4 text-xs">
                    <span className="font-bold">{testimonials[slide].n}</span>
                    <span className="text-white/35">/</span>
                    <span className="text-[#d4b16a]">{testimonials[slide].o}</span>
                  </div>
                </motion.div>
              </AnimatePresence>
              <div className="absolute bottom-0 right-0 flex gap-2">
                <button onClick={() => setSlide((slide + testimonials.length - 1) % testimonials.length)} className="grid size-10 place-items-center rounded-full border border-white/15 cursor-pointer" aria-label={lang === 'uz' ? 'Oldingi sharh' : 'Предыдущий отзыв'} data-testid="button-testimonial-prev">
                  <ChevronLeft size={16} />
                </button>
                <button onClick={() => setSlide((slide + 1) % testimonials.length)} className="grid size-10 place-items-center rounded-full border border-white/15 cursor-pointer" aria-label={lang === 'uz' ? 'Keyingi sharh' : 'Следующий отзыв'} data-testid="button-testimonial-next">
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          </div>
        </section>

        <Cta onEstimate={onEstimate} lang={lang} />
      </main>
    </>
  );
}

function Benefit({ icon, title, text }: { icon: ReactNode; title: string; text: string }) { 
  return (
    <div className="py-7 px-4 md:py-8 md:px-6 first:pl-0 last:pr-0">
      <div className="mb-5 text-[#d4b16a]">{icon}</div>
      <h3 className="font-display text-sm font-bold">{title}</h3>
      <p className="mt-2 text-xs leading-5 text-white/45">{text}</p>
    </div>
  ); 
}

function Stat({ value, label }: { value: string; label: string }) { 
  return (
    <div className="p-6 text-center md:p-10">
      <p className="font-display text-3xl font-extrabold text-[#d4b16a] md:text-5xl">
        <CountUp value={value} />
      </p>
      <p className="mx-auto mt-3 max-w-32.5 text-[10px] uppercase leading-4 tracking-wider text-white/45">{label}</p>
    </div>
  ); 
}

function Cta({ onEstimate, lang }: { onEstimate: (p?: string) => void; lang: Lang }) { 
  return (
    <section className="relative overflow-hidden px-6 sm:px-8 py-24 lg:px-12 xl:px-14">
      <div className="absolute inset-0 opacity-20" style={{ backgroundImage: `url(${pics.workshop})`, backgroundPosition: 'center', backgroundSize: 'cover' }} />
      <div className="absolute inset-0 bg-[#0d0d0d]/85" />
      <div className="relative mx-auto flex max-w-360 flex-col items-start justify-between gap-10 md:flex-row md:items-end">
        <div>
          <p className="mb-5 text-[10px] uppercase tracking-[.25em] text-[#d4b16a]">
            {lang === 'uz' ? '06 / Loyihani boshlash' : '06 / Начать проект'}
          </p>
          <h2 className="max-w-2xl font-display text-4xl font-extrabold leading-tight md:text-6xl">
            {lang === 'uz' ? <>Sizning o‘lchamingiz —<br />bizning aniq ishimiz.</> : <>Ваш проём —<br />наша точная работа.</>}
          </h2>
        </div>
        <button onClick={() => onEstimate()} className="gold-gradient flex shrink-0 items-center gap-3 rounded-full px-7 py-4 text-sm font-bold text-black cursor-pointer" data-testid="button-cta-estimate">
          {lang === 'uz' ? 'Loyihani muhokama qilish' : 'Обсудить проект'} <ArrowUpRight size={17} />
        </button>
      </div>
    </section>
  ); 
}

// --- LEVEL 1: MAIN CATEGORIES PAGE ---
function ProductsPage({ onEstimate, lang }: { onEstimate: (p?: string) => void; lang: Lang }) { 
  const t = copy(lang);
  return (
    <PageFrame 
      title={t.nav.products} 
      eyebrow={lang === 'uz' ? 'Tizimlar katalogi' : 'Каталог систем'} 
      intro={lang === 'uz' 
        ? 'Deraza, eshik, fasad, surma va interyer yechimlari — O‘zbekiston iqlimi, arxitekturasi va ishonchli foydalanish uchun loyihalangan.' 
        : 'Оконные, дверные, фасадные, раздвижные и интерьерные решения — спроектированы для климата, архитектуры и надежной эксплуатации в Узбекистане.'}
      lang={lang}
      canonicalPath="/products"
    >
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {categories.map((c, i) => {
          const count = products.filter(p => p.categorySlug === c.slug).length;
          const catTitle = t.categories[c.slug]?.title || c.title;
          const catSubtitle = t.categories[c.slug]?.subtitle || c.subtitle;
          return (
            <Link key={c.slug} href={`/products/${c.slug}`} className="group image-zoom relative min-h-85 overflow-hidden rounded-xl border border-white/10" data-testid={`card-category-${c.slug}`}>
              <img src={c.image} alt={catTitle} className="absolute inset-0 size-full object-cover opacity-60 transition duration-700 group-hover:scale-105" />
              <div className="absolute inset-0 bg-linear-to-t from-black via-black/30 to-transparent" />
              <div className="relative flex min-h-85 flex-col justify-end p-7">
                <div className="flex items-center justify-between">
                  <p className="text-[10px] uppercase tracking-[.25em] text-[#d4b16a]">0{i + 1} / system</p>
                  <span className="rounded-full bg-white/10 px-2.5 py-0.5 text-[10px] font-mono text-white/80">
                    <CountUp value={count} duration={0.8} /> {lang === 'uz' ? 'ta tizim' : count === 1 ? 'система' : count < 5 ? 'системы' : 'систем'}
                  </span>
                </div>
                <h2 className="mt-2 font-display text-2xl font-extrabold">{catTitle}</h2>
                <p className="mt-1.5 text-xs text-white/60 line-clamp-2">{catSubtitle}</p>
                <span className="mt-5 flex items-center gap-2 text-xs text-[#d4b16a] font-semibold">
                  {t.actions.details} <ArrowRight size={14} />
                </span>
              </div>
            </Link>
          );
        })}
      </div>
    </PageFrame>
  ); 
}

// --- LEVEL 2: CATEGORY PAGE WITH SUBCATEGORY FILTER & PRODUCT CARDS GRID ---
function CategoryProductsPage({ onEstimate, lang }: { onEstimate: (p?: string) => void; lang: Lang }) {
  const { categorySlug } = useParams<{ categorySlug: string }>();
  const category = categories.find(c => c.slug === categorySlug) ?? categories[0];
  const allItems = products
    .filter(p => p.categorySlug === category.slug)
    .map(p => getTranslatedProduct(p, lang));
  const t = copy(lang);

  const subcategories = Array.from(
    new Set(allItems.map(p => p.subcategory).filter(Boolean))
  ) as string[];

  const [selectedSub, setSelectedSub] = useState<string>('all');

  useEffect(() => {
    setSelectedSub('all');
  }, [categorySlug]);

  const items = selectedSub === 'all' 
    ? allItems 
    : allItems.filter(p => p.subcategory === selectedSub);

  const catTitle = t.categories[category.slug]?.title || category.title;
  const catDesc = t.categories[category.slug]?.description || category.description;

  return (
    <PageFrame 
      eyebrow={`${t.nav.products} / ${catTitle}`} 
      title={catTitle} 
      intro={catDesc}
      lang={lang}
      canonicalPath={`/products/${category.slug}`}
    >
      <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
        <Link href="/products" className="inline-flex items-center gap-2 text-xs font-semibold text-[#d4b16a] hover:underline">
          <ChevronLeft size={16} /> {lang === 'uz' ? 'Barcha toifalarga qaytish' : 'Вернуться ко всем категориям'}
        </Link>
        <span className="text-xs text-white/40">
          <CountUp value={items.length} duration={0.6} /> {lang === 'uz' ? 'ta tizim' : items.length === 1 ? 'система' : items.length < 5 ? 'системы' : 'систем'}
        </span>
      </div>

      {subcategories.length > 1 && (
        <div className="mb-8 flex flex-wrap gap-2">
          <button
            onClick={() => setSelectedSub('all')}
            className={`rounded-full border px-4 py-2 text-xs transition font-semibold ${
              selectedSub === 'all'
                ? 'border-[#c6a15b] bg-[#c6a15b] text-black shadow-md'
                : 'border-white/15 bg-[#151515] text-white/60 hover:border-[#c6a15b] hover:text-white'
            }`}
            data-testid="filter-sub-all"
          >
            {lang === 'uz' ? 'Barchasi' : 'Все'} (<CountUp value={allItems.length} duration={0.6} />)
          </button>
          {subcategories.map(sub => {
            const subCount = allItems.filter(p => p.subcategory === sub).length;
            return (
              <button
                key={sub}
                onClick={() => setSelectedSub(sub)}
                className={`rounded-full border px-4 py-2 text-xs transition font-semibold ${
                  selectedSub === sub
                    ? 'border-[#c6a15b] bg-[#c6a15b] text-black shadow-md'
                    : 'border-white/15 bg-[#151515] text-white/60 hover:border-[#c6a15b] hover:text-white'
                }`}
                data-testid={`filter-sub-${sub}`}
              >
                {sub} (<CountUp value={subCount} duration={0.6} />)
              </button>
            );
          })}
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {items.map((p) => (
          <Link 
            key={p.slug} 
            href={`/products/${category.slug}/${p.slug}`} 
            className="group flex flex-col justify-between overflow-hidden rounded-xl border border-white/10 bg-[#151515] transition duration-300 hover:border-[#c6a15b]"
            data-testid={`card-product-${p.slug}`}
          >
            <div className="relative aspect-16/10 overflow-hidden">
              <img src={p.image} alt={p.title} className="size-full object-cover transition duration-500 group-hover:scale-105 opacity-80" />
              <div className="absolute inset-0 bg-linear-to-t from-[#151515] via-transparent to-transparent" />
              <div className="absolute left-3 top-3 flex flex-wrap gap-1.5">
                <span className="rounded bg-[#c6a15b] px-2.5 py-1 text-[9px] font-bold text-black uppercase">
                  {p.brandCountry || p.brand}
                </span>
                {p.subcategory && (
                  <span className="rounded bg-black/60 backdrop-blur-sm border border-white/20 px-2 py-1 text-[9px] font-semibold text-white/90">
                    {p.subcategory}
                  </span>
                )}
              </div>
            </div>

            <div className="flex flex-1 flex-col justify-between p-6">
              <div>
                <p className="text-[10px] uppercase tracking-wider text-[#d4b16a] font-mono">{p.brand}</p>
                <h3 className="mt-1 font-display text-xl font-bold text-white group-hover:text-[#d4b16a] transition">
                  {p.title}
                </h3>
                <p className="mt-1.5 text-xs text-white/55 line-clamp-2">{p.subtitle}</p>

                <div className="mt-4 space-y-1.5">
                  {p.specs.slice(0, 4).map(s => (
                    <div key={s} className="flex items-start gap-2 text-[11px] text-white/70">
                      <Check size={12} className="text-[#c6a15b] shrink-0 mt-0.5" />
                      <span className="line-clamp-1">{s}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-6 flex items-center justify-between border-t border-white/10 pt-3">
                <span className="text-xs text-[#d4b16a] font-semibold">{t.actions.details}</span>
                <ArrowRight size={14} className="text-[#d4b16a] transition group-hover:translate-x-1" />
              </div>
            </div>
          </Link>
        ))}
      </div>
    </PageFrame>
  );
}

// --- LEVEL 3: PRODUCT DETAIL PAGE ---
function ProductDetail({ onEstimate, lang }: { onEstimate: (p?: string, c?: string) => void; lang: Lang }) { 
  const { categorySlug, productSlug, id } = useParams<{ categorySlug?: string; productSlug?: string; id?: string }>(); 
  const slug = productSlug || id;
  const rawProduct = products.find(p => p.slug === slug) ?? products[0]; 
  const product = getTranslatedProduct(rawProduct, lang);
  const category = categories.find(c => c.slug === product.categorySlug) ?? categories[0];
  const [selectedColor, setSelectedColor] = useState<WindowColor>(windowColors[0]);
  const t = copy(lang);

  const catTitle = t.categories[category.slug]?.title || category.title;

  const productSchema = {
    '@type': 'Product',
    name: product.title,
    description: product.description,
    image: product.image ? (product.image.startsWith('http') ? product.image : `https://allwindows.uz${product.image}`) : 'https://allwindows.uz/og.webp',
    brand: {
      '@type': 'Brand',
      name: product.brand || 'ALL WINDOWS'
    },
    category: catTitle,
    offers: {
      '@type': 'Offer',
      priceCurrency: 'UZS',
      availability: 'https://schema.org/InStock',
      url: `https://allwindows.uz/products/${category.slug}/${product.slug}`,
      seller: {
        '@type': 'Organization',
        name: 'ALL WINDOWS'
      }
    }
  };

  return (
    <PageFrame 
      title={product.title} 
      eyebrow={`${t.nav.products} / ${catTitle} / ${product.title}`} 
      intro={product.description}
      image={product.image}
      type="product"
      schema={productSchema}
      lang={lang}
      canonicalPath={`/products/${category.slug}/${product.slug}`}
    >
      <div className="mb-6 flex flex-wrap items-center gap-3">
        <Link href={`/products/${category.slug}`} className="inline-flex items-center gap-2 text-xs font-semibold text-[#d4b16a] hover:underline">
          <ChevronLeft size={16} /> {lang === 'uz' ? `«${catTitle}» tizimlariga qaytish` : `Назад к системам «${category.title}»`}
        </Link>
        {product.subcategory && (
          <span className="rounded-full bg-white/10 px-3 py-1 text-[10px] font-mono text-white/70">
            {product.subcategory}
          </span>
        )}
      </div>

      <div className="grid gap-8 lg:grid-cols-[0.85fr_1.15fr] items-start">
        <div className="overflow-hidden rounded-2xl border border-white/10 bg-[#121212] p-2.5 sm:p-3 shadow-lg">
          <img 
            src={product.image} 
            alt={product.title} 
            className="w-full max-h-75 sm:max-h-85 md:max-h-95 rounded-xl object-cover" 
          />
        </div>
        <div className="rounded-2xl border border-white/10 bg-[#151515] p-6 sm:p-7 flex flex-col justify-between shadow-xl">
          <div>
            <div className="flex items-center justify-between border-b border-white/10 pb-3.5">
              <p className="text-[10px] uppercase tracking-[.25em] text-[#d4b16a]">
                {lang === 'uz' ? 'Texnik xususiyatlar' : 'Технические характеристики'}
              </p>
              <span className="rounded bg-white/10 px-2.5 py-1 text-[10px] font-mono text-white/80">{product.brand}</span>
            </div>
            <div className="mt-3 divide-y divide-white/10">
              {product.specs.map(s => (
                <div key={s} className="flex items-start gap-2.5 py-2.5 text-xs sm:text-sm text-white/75">
                  <Check size={14} className="text-[#c6a15b] shrink-0 mt-0.5" />
                  <span>{s}</span>
                </div>
              ))}
            </div>

            <div className="mt-5 border-t border-white/10 pt-4">
              <ColorPicker
                selectedColorId={selectedColor.id}
                onSelectColor={setSelectedColor}
                maxVisible={6}
                variant="dark"
                lang={lang}
              />
            </div>
          </div>

          <button onClick={() => onEstimate(product.title, selectedColor.name)} className="gold-gradient mt-6 flex w-full items-center justify-center gap-2 rounded-full px-5 py-3 text-xs sm:text-sm font-bold text-black cursor-pointer hover:brightness-105 transition shadow-lg" data-testid="button-product-estimate">
            {lang === 'uz' ? 'Loyihani hisoblash' : 'Рассчитать проект'} <ArrowRight size={15} />
          </button>
        </div>
      </div>
      <div className="mt-14 sm:mt-16">
        <p className="mb-4 text-[10px] uppercase tracking-[.25em] text-[#d4b16a]">
          {lang === 'uz' ? 'Detallar va qo‘llanilishi' : 'Детали и применение'}
        </p>
        <div className="grid gap-3 sm:grid-cols-3">
          <img src={pics.detail} alt="Деталь оконной системы" className="h-36 sm:h-40 md:h-44 w-full rounded-xl object-cover border border-white/5" />
          <img src={pics.interior} alt="Интерьер с остеклением" className="h-36 sm:h-40 md:h-44 w-full rounded-xl object-cover border border-white/5" />
          <img src={pics.workshop} alt="Производство оконных систем" className="h-36 sm:h-40 md:h-44 w-full rounded-xl object-cover border border-white/5" />
        </div>
      </div>
      {product.related && product.related.length > 0 && (
        <div className="mt-20">
          <SectionIntro 
            eyebrow={lang === 'uz' ? 'Birga tanlashadi' : 'Выбирают вместе'} 
            title={lang === 'uz' ? 'To‘liq to‘plamni yig‘ing' : 'Соберите полный комплект'} 
          />
          <div className="grid gap-3 sm:grid-cols-2">
            {product.related.map(relSlug => { 
              const rawR = products.find(p => p.slug === relSlug);
              if (!rawR) return null;
              const r = getTranslatedProduct(rawR, lang);
              return (
                <Link key={r.slug} href={`/products/${r.categorySlug}/${r.slug}`} className="flex items-center gap-4 rounded-xl border border-white/10 bg-[#151515] p-4 transition hover:border-[#c6a15b]" data-testid={`link-related-${r.slug}`}>
                  <img src={r.image} alt={r.title} className="size-20 rounded-lg object-cover shrink-0" />
                  <div>
                    <h3 className="font-display text-base font-bold">{r.title}</h3>
                    <p className="mt-1 text-xs text-white/45 line-clamp-1">{r.subtitle}</p>
                  </div>
                  <ArrowUpRight className="ml-auto text-[#c6a15b] shrink-0" size={16} />
                </Link>
              ); 
            })}
          </div>
        </div>
      )}
    </PageFrame>
  ); 
}

function PageFrame({ 
  eyebrow, 
  title, 
  intro, 
  image, 
  type = 'website', 
  schema, 
  lang, 
  canonicalPath, 
  children 
}: { 
  eyebrow: string; 
  title: string; 
  intro: string; 
  image?: string; 
  type?: 'website' | 'article' | 'product'; 
  schema?: any; 
  lang?: Lang; 
  canonicalPath?: string; 
  children: ReactNode 
}) { 
  return (
    <>
      <SEO 
        title={`${title} — ALL WINDOWS`} 
        description={intro} 
        image={image || '/og.webp'} 
        type={type} 
        schema={schema} 
        lang={lang} 
        canonicalPath={canonicalPath} 
      />
      <main className="mx-auto max-w-360 px-6 sm:px-8 pb-24 pt-28 sm:pt-32 md:pt-36 lg:px-12 xl:px-14">
        <div className="mb-10 sm:mb-12 max-w-4xl">
          <p className="mb-4 flex items-center gap-4 text-[10px] uppercase tracking-[.28em] text-[#d4b16a]">
            {eyebrow}<span className="h-px w-16 bg-[#c6a15b]" />
          </p>
          <h1 className="font-display text-4xl sm:text-6xl font-extrabold leading-none tracking-tighter md:text-7xl lg:text-8xl">{title}</h1>
          <p className="mt-5 max-w-2xl text-sm sm:text-base leading-relaxed text-white/55">{intro}</p>
        </div>
        {children}
      </main>
    </>
  ); 
}

function ProjectsPage({ onEstimate, lang }: { onEstimate?: (p?: string) => void; lang: Lang }) { 
  const params = useParams<{ slug?: string }>();
  const t = copy(lang);
  const defaultFilter = lang === 'uz' ? 'Barchasi' : 'Все';
  const [filter, setFilter] = useState(defaultFilter); 
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);

  useEffect(() => {
    setFilter(lang === 'uz' ? 'Barchasi' : 'Все');
  }, [lang]);

  useEffect(() => {
    if (params?.slug) {
      const match = projects.find(p => p.slug === params.slug);
      if (match && !match.isTextOnly && match.images.length > 0) {
        setSelectedProject(match);
      }
    }
  }, [params?.slug]);

  const filters = lang === 'uz' 
    ? ['Barchasi', 'Turar-joy majmualari', 'Tijorat obyektlari', 'Ijtimoiy obyektlar', 'Xususiy obyektlar']
    : ['Все', 'Жилые комплексы', 'Коммерческие', 'Социальные объекты', 'Частные объекты'];

  const typeMap: Record<string, string> = {
    'Жилые комплексы': 'Turar-joy majmualari',
    'Коммерческие': 'Tijorat obyektlari',
    'Социальные объекты': 'Ijtimoiy obyektlar',
    'Частные объекты': 'Xususiy obyektlar',
  };

  const isMatch = (pType: string, f: string) => {
    if (f === 'Все' || f === 'Barchasi') return true;
    if (f === pType) return true;
    if (lang === 'uz' && typeMap[pType] === f) return true;
    return false;
  };

  const shown = projects.filter(p => isMatch(p.type, filter)); 

  return (
    <PageFrame 
      eyebrow={lang === 'uz' ? 'Portfel' : 'Портфолио'} 
      title={t.nav.projects} 
      intro={lang === 'uz'
        ? 'ALL WINDOWS jamoasi tomonidan Toshkentda amalga oshirilgan real fasadlar, derazalar va kirish guruhlari. Fotogalereyani ochish uchun istalgan obyekt ustiga bosing.'
        : 'Реальные фасады, окна и входные группы, выполненные командой ALL WINDOWS в Ташкенте. Нажмите на любой объект, чтобы открыть фотогалерею.'}
      lang={lang}
      canonicalPath="/projects"
    >
      <div className="mb-8 flex flex-wrap gap-2">
        {filters.map(f => {
          const count = projects.filter(p => isMatch(p.type, f)).length;
          return (
            <button 
              key={f} 
              onClick={() => setFilter(f)} 
              className={`rounded-full border px-4 py-2 text-xs transition flex items-center gap-1.5 cursor-pointer ${filter === f ? 'border-[#c6a15b] bg-[#c6a15b] text-black font-semibold' : 'border-white/15 text-white/55 hover:border-[#c6a15b]'}`} 
              data-testid={`button-filter-${f}`}
            >
              <span>{f}</span>
              <span className={`text-[10px] rounded-full px-1.5 py-0.5 ${filter === f ? 'bg-black/20 text-black font-bold' : 'bg-white/10 text-white/60'}`}>
                {count}
              </span>
            </button>
          );
        })}
      </div>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {shown.map(p => {
          const projectTypeLabel = lang === 'uz' ? (typeMap[p.type] || p.type) : p.type;
          if (p.isTextOnly || !p.images || p.images.length === 0) {
            return (
              <div
                key={p.slug}
                className="relative flex min-h-[370px] flex-col justify-between overflow-hidden rounded-xl border border-[#c6a15b]/35 bg-linear-to-br from-[#1c1914] via-[#141414] to-[#0e0e0e] p-7 shadow-xl"
                data-testid={`card-project-${p.slug}`}
              >
                <div className="pointer-events-none absolute -right-3 -bottom-6 select-none font-display text-[120px] font-black text-[#c6a15b]/5 leading-none">
                  350+
                </div>

                <div>
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] uppercase tracking-[.25em] text-[#d4b16a]">
                      {projectTypeLabel} · {p.city}
                    </span>
                    <span className="rounded-full border border-[#c6a15b]/40 bg-[#c6a15b]/15 px-2.5 py-0.5 text-[10px] font-semibold text-[#d4b16a]">
                      {lang === 'uz' ? '350+ obyekt' : '350+ объектов'}
                    </span>
                  </div>
                  <h2 className="mt-4 font-display text-2xl sm:text-3xl font-extrabold text-white">
                    {p.title}
                  </h2>
                  <p className="mt-4 text-sm leading-relaxed text-white/70">
                    {lang === 'uz' 
                      ? 'Toshkent shahri va Toshkent viloyatida xususiy qarorgohlar, kottejlar va dala hovlilarni oynalash.'
                      : 'Остекление частных резиденций, коттеджей и загородных домов в Ташкенте и Ташкентской области.'}
                  </p>
                </div>

                <div className="mt-6 border-t border-white/10 pt-4">
                  {onEstimate && (
                    <button 
                      onClick={() => onEstimate('Остекление частного дома')} 
                      className="w-full flex items-center justify-center gap-2 rounded-lg bg-[#c6a15b]/15 hover:bg-[#c6a15b] hover:text-black py-3 text-xs font-bold text-[#d4b16a] border border-[#c6a15b]/40 transition cursor-pointer"
                    >
                      {lang === 'uz' ? 'Uy uchun hisob-kitob buyurtma qilish' : 'Заказать расчёт для дома'} <ArrowRight size={14} />
                    </button>
                  )}
                </div>
              </div>
            );
          }

          return (
            <div 
              key={p.slug} 
              onClick={() => setSelectedProject(p)} 
              className="group image-zoom relative min-h-[370px] overflow-hidden rounded-xl cursor-pointer border border-white/10 bg-[#141414] transition duration-300 hover:border-[#c6a15b]/60 hover:shadow-2xl hover:shadow-[#c6a15b]/10" 
              data-testid={`card-project-${p.slug}`}
            >
              <img src={p.image} alt={p.title} className="absolute inset-0 size-full object-cover transition-transform duration-500 group-hover:scale-105" />
              <div className="absolute inset-0 bg-linear-to-t from-black/95 via-black/45 to-black/20" />
              
              <div className="absolute top-3.5 right-3.5 rounded-full bg-black/70 backdrop-blur-md px-2.5 py-1 text-[11px] text-white/90 border border-white/15 flex items-center gap-1.5 shadow-lg">
                <Images size={12} className="text-[#d4b16a]" />
                <span>{t.projectsSection.photosCount(p.images.length)}</span>
              </div>

              <div className="relative flex min-h-[370px] flex-col justify-end p-6">
                <p className="text-[10px] uppercase tracking-[.25em] text-[#d4b16a]">{projectTypeLabel} · {p.city}</p>
                <h2 className="mt-1 font-display text-2xl font-extrabold text-white group-hover:text-[#d4b16a] transition">{p.title}</h2>
                <div className="mt-4 flex items-center gap-2 text-xs font-semibold text-[#d4b16a]">
                  <span>{t.actions.openGallery}</span>
                  <Maximize2 size={13} className="transition group-hover:translate-x-1" />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <ProjectGalleryModal project={selectedProject} onClose={() => setSelectedProject(null)} />
    </PageFrame>
  ); 
}

function AboutPage({ lang }: { lang: Lang }) { 
  const [certModal, setCertModal] = useState(false);
  const t = copy(lang);

  return (
    <PageFrame 
      eyebrow={t.about.eyebrow} 
      title={t.about.title} 
      intro={t.about.intro}
      lang={lang}
      canonicalPath="/about"
    >
      <div className="grid gap-3 md:grid-cols-2">
        <img src={pics.workshop} alt="Производственный цех ALL WINDOWS" className="h-112.5 w-full rounded-xl object-cover" />
        <div className="rounded-xl bg-[#d9c8a3] p-8 text-[#151515] md:p-12">
          <p className="text-[10px] uppercase tracking-[.25em] text-[#816a3f]">{t.about.approachTitle}</p>
          <p className="mt-8 font-display text-3xl font-extrabold leading-tight">{t.about.approachHeading}</p>
          <p className="mt-7 text-sm leading-7 text-black/60">
            {t.about.approachText}
          </p>
        </div>
      </div>
      
      <div className="my-24 grid gap-10 md:grid-cols-3">
        <div>
          <Factory className="text-[#c6a15b]" />
          <h2 className="mt-5 font-display text-xl font-bold">{t.about.feat1Title}</h2>
          <p className="mt-3 text-sm leading-6 text-white/50">{t.about.feat1Text}</p>
        </div>
        <div>
          <Zap className="text-[#c6a15b]" />
          <h2 className="mt-5 font-display text-xl font-bold">{t.about.feat2Title}</h2>
          <p className="mt-3 text-sm leading-6 text-white/50">{t.about.feat2Text}</p>
        </div>
        <div>
          <GlassWater className="text-[#c6a15b]" />
          <h2 className="mt-5 font-display text-xl font-bold">{t.about.feat3Title}</h2>
          <p className="mt-3 text-sm leading-6 text-white/50">{t.about.feat3Text}</p>
        </div>
      </div>

      <div className="mt-16 border-t border-white/10 pt-16">
        <SectionIntro eyebrow={t.about.certEyebrow} title={t.about.certTitle} />
        
        <div className="mt-10 grid gap-6 lg:grid-cols-[380px_1fr] lg:items-stretch xl:grid-cols-[400px_1fr]">
          <div 
            onClick={() => setCertModal(true)}
            className="group relative flex flex-col justify-between cursor-pointer overflow-hidden rounded-2xl border border-[#c6a15b]/30 bg-linear-to-b from-[#1c1c1c] to-[#121212] p-4 sm:p-5 shadow-2xl transition duration-500 hover:border-[#c6a15b] hover:shadow-[0_0_35px_rgba(198,161,91,0.2)] h-full w-full max-w-105 mx-auto lg:max-w-none"
            data-testid="certificate-card"
          >
            <div className="relative flex-1 min-h-90 flex items-center justify-center overflow-hidden rounded-xl border border-white/10 bg-black/40 p-2">
              <img 
                src={pics.certificate1 || '/imgs/certificate1.webp'} 
                alt="Сертификат соответствия ALL WINDOWS" 
                className="max-h-115 w-full object-contain rounded-lg transition duration-500 group-hover:scale-[1.02]"
                onError={(e) => {
                  const target = e.currentTarget as HTMLImageElement;
                  if (target.src !== window.location.origin + '/imgs/certificate1.webp') {
                    target.src = '/imgs/certificate1.webp';
                  }
                }}
              />
              <div className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 backdrop-blur-[2px] transition duration-300 group-hover:opacity-100">
                <span className="flex items-center gap-2 rounded-full border border-[#c6a15b]/50 bg-black/80 px-4 py-2 text-xs font-semibold text-[#d4b16a] shadow-lg">
                  <Maximize2 size={15} /> {t.actions.enlarge}
                </span>
              </div>
            </div>

            <div className="mt-4 flex items-center justify-between px-1 shrink-0">
              <div className="flex items-center gap-2.5">
                <Award className="text-[#c6a15b]" size={18} />
                <div>
                  <p className="text-xs font-bold text-white">{t.about.certCardTitle}</p>
                  <p className="text-[11px] text-white/45">{t.about.certCardDesc}</p>
                </div>
              </div>
              <span className="rounded-full bg-[#c6a15b]/15 px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-[#d4b16a] border border-[#c6a15b]/30">
                {t.actions.verified}
              </span>
            </div>
          </div>

          <div className="flex flex-col justify-between gap-4 h-full">
            <div className="flex-1 flex flex-col justify-center rounded-xl border border-white/10 bg-[#151515] p-6 transition duration-300 hover:border-[#c6a15b]/40">
              <div className="flex items-start gap-4">
                <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-[#c6a15b]/10 text-[#c6a15b]">
                  <FileCheck2 size={20} />
                </div>
                <div>
                  <h3 className="text-base font-bold text-white">{t.about.certPoint1Title}</h3>
                  <p className="mt-2 text-xs leading-relaxed text-white/55">
                    {t.about.certPoint1Text}
                  </p>
                </div>
              </div>
            </div>

            <div className="flex-1 flex flex-col justify-center rounded-xl border border-white/10 bg-[#151515] p-6 transition duration-300 hover:border-[#c6a15b]/40">
              <div className="flex items-start gap-4">
                <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-[#c6a15b]/10 text-[#c6a15b]">
                  <ShieldCheck size={20} />
                </div>
                <div>
                  <h3 className="text-base font-bold text-white">{t.about.certPoint2Title}</h3>
                  <p className="mt-2 text-xs leading-relaxed text-white/55">
                    {t.about.certPoint2Text}
                  </p>
                </div>
              </div>
            </div>

            <div className="flex-1 flex flex-col justify-center rounded-xl border border-white/10 bg-[#151515] p-6 transition duration-300 hover:border-[#c6a15b]/40">
              <div className="flex items-start gap-4">
                <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-[#c6a15b]/10 text-[#c6a15b]">
                  <CheckCircle2 size={20} />
                </div>
                <div>
                  <h3 className="text-base font-bold text-white">{t.about.certPoint3Title}</h3>
                  <p className="mt-2 text-xs leading-relaxed text-white/55">
                    {t.about.certPoint3Text}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {certModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4 backdrop-blur-md"
            onClick={() => setCertModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="relative max-h-[90vh] max-w-4xl overflow-hidden rounded-2xl border border-[#c6a15b]/40 bg-[#141414] p-3 shadow-2xl"
              onClick={e => e.stopPropagation()}
            >
              <button
                onClick={() => setCertModal(false)}
                className="absolute right-5 top-5 z-10 flex size-9 items-center justify-center rounded-full bg-black/70 text-white/80 transition hover:bg-black hover:text-white"
                aria-label={lang === 'uz' ? 'Yopish' : 'Закрыть'}
              >
                <X size={20} />
              </button>
              <img
                src={pics.certificate1 || '/imgs/certificate1.webp'}
                alt="Сертификат соответствия ALL WINDOWS"
                className="max-h-[85vh] w-auto rounded-xl object-contain mx-auto"
                onError={(e) => {
                  const target = e.currentTarget as HTMLImageElement;
                  if (target.src !== window.location.origin + '/imgs/certificate1.webp') {
                    target.src = '/imgs/certificate1.webp';
                  }
                }}
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </PageFrame>
  ); 
}

function ServicesPage({ lang }: { lang: Lang }) { 
  const t = copy(lang);
  const icons = [Ruler, FileCheck2, Factory, Send, Wrench, ShieldCheck];
  const services = t.services.items.map((item, i) => ({
    icon: icons[i] || Wrench,
    title: item.title,
    text: item.text,
  }));

  return (
    <PageFrame 
      eyebrow={t.services.eyebrow} 
      title={t.services.title} 
      intro={t.services.intro}
      lang={lang}
      canonicalPath="/services"
    >
      <div className="grid gap-px overflow-hidden rounded-xl border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-3">
        {services.map((s, i) => (
          <div key={s.title} className="bg-[#151515] p-7 md:p-9">
            <s.icon className="text-[#c6a15b]" size={22} />
            <p className="mt-12 text-[10px] text-[#c6a15b]">0{i + 1}</p>
            <h2 className="mt-3 font-display text-xl font-bold">{s.title}</h2>
            <p className="mt-3 text-sm leading-6 text-white/50">{s.text}</p>
          </div>
        ))}
      </div>
      <div className="mt-16 flex flex-col justify-between gap-8 rounded-xl bg-[#d9c8a3] p-8 text-[#151515] md:flex-row md:items-end md:p-12">
        <div>
          <p className="text-[10px] uppercase tracking-[.25em] text-[#816a3f]">{t.services.bannerEyebrow}</p>
          <h2 className="mt-5 max-w-xl font-display text-3xl font-extrabold">{t.services.bannerTitle}</h2>
        </div>
        <Link href="/contacts" className="flex items-center gap-2 text-sm font-bold" data-testid="link-services-contacts">
          {t.services.bannerBtn} <ArrowRight size={16} />
        </Link>
      </div>
    </PageFrame>
  ); 
}

function ContactsPage({ onEstimate, lang }: { onEstimate: (p?: string) => void; lang: Lang }) { 
  const t = copy(lang);
  return (
    <PageFrame 
      eyebrow={t.contacts.eyebrow} 
      title={t.contacts.title} 
      intro={t.contacts.intro}
      lang={lang}
      canonicalPath="/contacts"
    >
      <div className="grid gap-3 lg:grid-cols-[.8fr_1.2fr]">
        <div className="flex flex-col justify-between rounded-xl bg-[#d9c8a3] p-8 text-[#151515] md:p-10 shadow-sm">
          <div>
            <p className="text-[10px] uppercase tracking-[.25em] text-[#816a3f]">{t.contacts.showroomTitle}</p>
            <p className="mt-7 font-display text-2xl md:text-3xl font-extrabold leading-tight whitespace-pre-line">
              {t.contacts.address}
            </p>
            
            <div className="mt-8 space-y-4 border-t border-black/20 pt-6 text-sm">
              <a href="tel:+998888001800" className="flex items-center gap-3 font-bold transition hover:opacity-80" data-testid="link-contact-phone">
                <Phone size={16} /> +998 88 800 18 00
              </a>
              <a href="https://t.me/all_windows" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 transition hover:opacity-80" data-testid="link-contact-telegram">
                <Send size={16} /> @all_windows
              </a>
              <a href="https://instagram.com/all_windows" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 transition hover:opacity-80">
                <Instagram size={16} /> @all_windows
              </a>
            </div>

            <div className="mt-8 pt-2">
              <button
                type="button"
                onClick={() => onEstimate()}
                className="flex w-full items-center justify-center rounded-full bg-[#151515] px-6 py-3.5 text-xs sm:text-sm font-bold text-white transition hover:bg-black active:scale-[0.99] cursor-pointer shadow-md"
                data-testid="button-open-contact-modal"
              >
                {t.contacts.btn}
              </button>
            </div>
          </div>

          <div className="mt-8 border-t border-black/15 pt-4 text-xs text-black/60">
            <p className="font-semibold text-black/80">{t.contacts.scheduleTitle}</p>
            <p className="mt-1 whitespace-pre-line">{t.contacts.scheduleText}</p>
          </div>
        </div>

        <div className="relative min-h-95 md:min-h-110 overflow-hidden rounded-xl border border-white/10 bg-[#171717]">
          <iframe
            title="ALL WINDOWS Локация на карте"
            src="https://maps.google.com/maps?q=41.224865,69.228613&hl=ru&z=17&ie=UTF8&iwloc=&output=embed"
            className="h-full w-full min-h-95 md:min-h-110 border-0 filter invert-[0.88] hue-rotate-180 contrast-[1.1] opacity-85"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
          <div className="absolute left-4 top-4 z-10 flex items-center gap-2 rounded-xl border border-[#c6a15b]/40 bg-[#121212]/90 px-3.5 py-2 backdrop-blur-md shadow-lg pointer-events-auto">
            <MapPin size={16} className="text-[#c6a15b]" />
            <span className="text-xs font-semibold text-white">
              {lang === 'uz' ? 'Toshkent shahri, Bag‘rikeng ko‘chasi' : 'Ташкент, улица Багрикенг'}
            </span>
          </div>
          <div className="absolute right-4 top-4 z-10">
            <a
              href="https://www.google.com/maps/place/41%C2%B013'29.5%22N+69%C2%B013'43.0%22E/@41.224865,69.228613,18z"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 rounded-xl border border-white/15 bg-black/80 px-3 py-2 text-xs font-medium text-white/80 backdrop-blur-md transition hover:border-[#c6a15b] hover:text-white shadow-lg"
            >
              <ExternalLink size={13} /> {t.actions.openMap}
            </a>
          </div>
        </div>
      </div>
    </PageFrame>
  ); 
}

function RouterView({ lang, onEstimate }: { lang: Lang; onEstimate: (p?: string, c?: string) => void }) { 
  return (
    <Switch>
      <Route path="/" component={() => <Home lang={lang} onEstimate={onEstimate} />} />
      <Route path="/about" component={() => <AboutPage lang={lang} />} />
      <Route path="/products" component={() => <ProductsPage lang={lang} onEstimate={onEstimate} />} />
      <Route path="/products/:categorySlug/:productSlug" component={() => <ProductDetail lang={lang} onEstimate={onEstimate} />} />
      <Route path="/products/:categorySlug" component={() => <CategoryProductsPage lang={lang} onEstimate={onEstimate} />} />
      <Route path="/projects" component={() => <ProjectsPage lang={lang} onEstimate={onEstimate} />} />
      <Route path="/projects/:slug" component={() => <ProjectsPage lang={lang} onEstimate={onEstimate} />} />
      <Route path="/services" component={() => <ServicesPage lang={lang} />} />
      <Route path="/contacts" component={() => <ContactsPage lang={lang} onEstimate={onEstimate} />} />
      <Route component={NotFound} />
    </Switch>
  ); 
}

function App() { 
  const [lang, setLang] = useState<Lang>(() => {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      const urlLang = params.get('lang');
      if (urlLang === 'uz' || urlLang === 'ru') return urlLang;
      const saved = localStorage.getItem('all_windows_lang');
      if (saved === 'uz' || saved === 'ru') return saved as Lang;
    }
    return 'ru';
  }); 

  const handleSetLang = (newLang: Lang) => {
    setLang(newLang);
    if (typeof window !== 'undefined') {
      try {
        localStorage.setItem('all_windows_lang', newLang);
        const url = new URL(window.location.href);
        if (newLang === 'uz') {
          url.searchParams.set('lang', 'uz');
        } else {
          url.searchParams.delete('lang');
        }
        window.history.replaceState({}, '', url.pathname + url.search + url.hash);
      } catch {
        // ignore
      }
    }
  };

  const [modal, setModal] = useState(false); 
  const [initialProduct, setInitialProduct] = useState(''); 
  const [initialColor, setInitialColor] = useState(''); 
  
  const openEstimate = (p = '', c = '') => { 
    setInitialProduct(p); 
    setInitialColor(c); 
    setModal(true); 
  }; 

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <ErrorBoundary>
          <Shell lang={lang} setLang={handleSetLang} onEstimate={openEstimate}>
            <RouterView lang={lang} onEstimate={openEstimate} />
          </Shell>
        </ErrorBoundary>
        <Toaster />
      </TooltipProvider>
      <EstimateModal open={modal} lang={lang} initialProduct={initialProduct} initialColor={initialColor} onClose={() => setModal(false)} />
    </QueryClientProvider>
  ); 
}

export default App;
